import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  Grid,
  Divider,
  Tab,
  Tabs,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack,
  NavigateBefore,
  NavigateNext,
  CheckCircle as CheckIcon,
  Pages as PagesIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  SwapHoriz as SwapHorizIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  RestartAlt as ResetIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  SwapVert as MoveToIcon,
  ViewQuilt as ChangeLayoutIcon,
  OpenWith as MoveSpreadIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  CallSplit as SplitIcon,
} from '@mui/icons-material';
import albumService from '../../api/albumService';
import sessionService from '../../api/sessionService';
import imageService from '../../api/imageService';
import AuthenticatedImage from '../AuthenticatedImage';
import ImageAnalysisDetailsDialog from '../ImageAnalysisDetailsDialog';
import { loadAuthenticatedImage } from '../../utils/imageUtils';
import { getServerUrl } from '../../config/runtimeConfig';
import { ALBUM_PAGE_TEMPLATES, ALBUM_TEMPLATE_CATEGORIES } from './albumPageTemplates';

const API_URL = getServerUrl();

// ── Zone → mm slot conversion (mirrors backend formatTemplatesForPrompt) ──
const DIALOG_ZONE_GAP = 4; // mm inset on each side between adjacent zones
function zoneToSlot(z, titleStripMm = 0) {
  const contentH = 210 - titleStripMm;
  return {
    name: z.role,
    x:      Math.round(z.xPct * 297) + DIALOG_ZONE_GAP,
    y:      Math.round(z.yPct * contentH) + titleStripMm + DIALOG_ZONE_GAP,
    width:  Math.max(10, Math.round(z.wPct * 297) - DIALOG_ZONE_GAP * 2),
    height: Math.max(10, Math.round(z.hPct * contentH) - DIALOG_ZONE_GAP * 2),
  };
}

function slotCountToCategory(n) {
  if (n <= 1) return '1';
  if (n === 2) return '2';
  if (n === 3) return '3';
  if (n <= 5) return '4-5';
  return '6+';
}

const PREVIEW_W_PX = 794;
const SCALE = PREVIEW_W_PX / 297;
const PREVIEW_H_PX = Math.round(210 * SCALE);

const DEFAULT_DISPLAY_SCALE = 0.62;
const PAGE_LABEL_HEIGHT_PX = 36;
const PREVIEW_GAP_PX = 8;
const PAGE_TURN_DURATION_MS = 1400;
const PRE_TURN_SPINNER_MIN_MS = 180;

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
const formatDate = (d) => { try { return d ? new Date(d).toLocaleString('en-GB') : 'Unknown'; } catch { return 'Invalid Date'; } };

const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => resolve());
});

const isEditableTarget = (target) => {
  if (!(target instanceof Element)) return false;
  const tagName = target.tagName?.toLowerCase();
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return true;
  if (target.isContentEditable) return true;
  return Boolean(target.closest('[contenteditable="true"], [role="textbox"], [role="combobox"], [role="listbox"]'));
};

// Mini SVG thumbnail for a single-page layout (accepts slots: {x,y,width,height}[])
const LayoutThumbnail = ({ slots = [], selected = false }) => {
  const W = 100, H = 67, pW = 297, pH = 210;
  const scaleX = W / pW, scaleY = H / pH;
  return (
    <svg
      width={W} height={H}
      style={{ display: 'block', border: `2px solid ${selected ? '#1976d2' : '#ccc'}`, borderRadius: 4, background: '#f8f5f0', flexShrink: 0 }}
    >
      {(slots || []).map((slot, i) => (
        <rect
          key={i}
          x={(slot.x ?? 0) * scaleX} y={(slot.y ?? 0) * scaleY}
          width={(slot.width ?? 0) * scaleX} height={(slot.height ?? 0) * scaleY}
          fill="#90caf9" stroke="#1976d2" strokeWidth={1} rx={2}
        />
      ))}
    </svg>
  );
};

const buildFilterCss = ({ brightness = 100, contrast = 100, saturation = 100, hue = 0, grayscale = 0, sepia = 0 } = {}) => {
  const parts = [];
  if (brightness !== 100) parts.push(`brightness(${brightness}%)`);
  if (contrast !== 100) parts.push(`contrast(${contrast}%)`);
  if (saturation !== 100) parts.push(`saturate(${saturation}%)`);
  if (hue !== 0) parts.push(`hue-rotate(${hue}deg)`);
  if (grayscale !== 0) parts.push(`grayscale(${grayscale}%)`);
  if (sepia !== 0) parts.push(`sepia(${sepia}%)`);
  return parts.length ? parts.join(' ') : undefined;
};

const PagePreview = ({ page, backgroundColor, pageMarginMm = 0, onSlotClick, selectedSlotIndex = null, preloadedImageUrls = null, onSlotDrag = null, onSlotZoom = null, onSlotRotate = null }) => {
  const dragStartRef = useRef(null);
  const bg = page.backgroundColor || backgroundColor || '#f8f5f0';
  const marginPx = Math.round(pageMarginMm * SCALE);

  return (
    <Box
      sx={{
        position: 'relative',
        width: PREVIEW_W_PX,
        height: PREVIEW_H_PX,
        backgroundColor: bg,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: 'grey.400',
        boxShadow: 4,
        flexShrink: 0,
      }}
    >
      {(page.images || []).map((slot, si) => {
        const x = Math.round(slot.x * SCALE);
        const y = Math.round(slot.y * SCALE);
        const w = Math.round(slot.width * SCALE);
        const h = Math.round(slot.height * SCALE);
        const zoom = slot.zoom || 1;
        const focusX = slot.focusX ?? 0.5;
        const focusY = slot.focusY ?? 0.5;
        const rotate = slot.rotate || 0;
        // Scale image up enough to fully cover the slot's corners after rotation,
        // so there are no blank triangles when the image is rotated.
        const rotRad = rotate * Math.PI / 180;
        const cosA = Math.abs(Math.cos(rotRad));
        const sinA = Math.abs(Math.sin(rotRad));
        const rotScale = rotate !== 0
          ? Math.max((w * cosA + h * sinA) / w, (w * sinA + h * cosA) / h)
          : 1;
        const effectiveZoom = zoom * rotScale;
        const leftPctRaw = (0.5 - (focusX * effectiveZoom)) * 100;
        const topPctRaw = (0.5 - (focusY * effectiveZoom)) * 100;
        const minOffsetPct = -(effectiveZoom - 1) * 100;
        const leftPct = Math.max(minOffsetPct, Math.min(0, leftPctRaw));
        const topPct = Math.max(minOffsetPct, Math.min(0, topPctRaw));
        const radius = Math.round((slot.borderRadius || 0) * SCALE);
        const imageId = slot._meta?.imageId || slot.imageId;
        const imageIdKey = imageId ? String(imageId) : '';
        const preloadedSrc = imageIdKey ? preloadedImageUrls?.[imageIdKey] : null;

        return (
          <Box
            key={si}
            onClick={(e) => { if (onSlotClick) { e.stopPropagation(); onSlotClick(si); } }}
            sx={{
              position: 'absolute',
              left: x,
              top: y,
              width: w,
              height: h,
              overflow: 'hidden',
              zIndex: slot.zIndex ?? 0,
              borderRadius: `${radius}px`,
              bgcolor: 'grey.200',
              outline: selectedSlotIndex === si ? '3px solid #1976d2' : '2px solid white',
              outlineOffset: '-1px',
              boxSizing: 'border-box',
              cursor: onSlotClick ? 'pointer' : 'default',
            }}
          >
            {imageId ? (
              <Box sx={{ position: 'absolute', inset: 0, transform: rotate ? `rotate(${rotate}deg)` : undefined, filter: buildFilterCss(slot.filters || {}) || undefined }}>
                {/* No overflow:hidden here — outer slot box clips; image scaled up via effectiveZoom to cover corners */}
                {preloadedSrc ? (
                  <Box
                    component="img"
                    src={preloadedSrc}
                    alt={slot._meta?.description || ''}
                    sx={{
                      position: 'absolute',
                      width: `${effectiveZoom * 100}%`,
                      height: `${effectiveZoom * 100}%`,
                      objectFit: 'cover',
                      objectPosition: `${focusX * 100}% ${focusY * 100}%`,
                      left: `${leftPct}%`,
                      top: `${topPct}%`,
                      display: 'block',
                    }}
                  />
                ) : (
                  <AuthenticatedImage
                    imageId={imageId}
                    type="serve"
                    alt={slot._meta?.description || ''}
                    sx={{
                      position: 'absolute',
                      width: `${effectiveZoom * 100}%`,
                      height: `${effectiveZoom * 100}%`,
                      objectFit: 'cover',
                      objectPosition: `${focusX * 100}% ${focusY * 100}%`,
                      left: `${leftPct}%`,
                      top: `${topPct}%`,
                      display: 'block',
                    }}
                  />
                )}
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>No image</Typography>
              </Box>
            )}
            {selectedSlotIndex === si && (onSlotDrag || onSlotZoom || onSlotRotate) && (
              <>
                {/* ── Center handle: pan/focus ── */}
                {onSlotDrag && (
                  <Box
                    component="span"
                    title="Drag to reposition"
                    sx={{
                      position: 'absolute', left: '50%', top: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: 28, height: 28, borderRadius: '50%',
                      bgcolor: 'primary.main', opacity: 0.85, cursor: 'grab', zIndex: 100,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.6)', border: '2px solid white',
                      touchAction: 'none',
                      '&:active': { cursor: 'grabbing', opacity: 1 },
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect() || { width: 1, height: 1 };
                      dragStartRef.current = { type: 'pan', x: e.clientX, y: e.clientY, w: rect.width, h: rect.height };
                    }}
                    onPointerMove={(e) => {
                      if (!dragStartRef.current || dragStartRef.current.type !== 'pan') return;
                      const dx = e.clientX - dragStartRef.current.x;
                      const dy = e.clientY - dragStartRef.current.y;
                      dragStartRef.current.x = e.clientX;
                      dragStartRef.current.y = e.clientY;
                      onSlotDrag(si, dx / dragStartRef.current.w, dy / dragStartRef.current.h);
                    }}
                    onPointerUp={(e) => { e.currentTarget.releasePointerCapture(e.pointerId); dragStartRef.current = null; }}
                  />
                )}

                {/* ── Corner handles: zoom ── */}
                {onSlotZoom && [['0%','0%',-1,-1],['100%','0%',1,-1],['0%','100%',-1,1],['100%','100%',1,1]].map(([cx, cy, outX, outY], ci) => (
                  <Box
                    key={ci}
                    component="span"
                    title="Drag to zoom"
                    sx={{
                      position: 'absolute', left: cx, top: cy,
                      transform: 'translate(-50%, -50%)',
                      width: 14, height: 14,
                      bgcolor: 'warning.main', opacity: 0.9, zIndex: 100,
                      boxShadow: '0 1px 5px rgba(0,0,0,0.55)', border: '2px solid white',
                      borderRadius: '3px',
                      cursor: 'nwse-resize',
                      touchAction: 'none',
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect() || { width: 1, height: 1 };
                      dragStartRef.current = { type: 'zoom', x: e.clientX, y: e.clientY, w: rect.width, h: rect.height, outX, outY };
                    }}
                    onPointerMove={(e) => {
                      if (!dragStartRef.current || dragStartRef.current.type !== 'zoom') return;
                      const dx = e.clientX - dragStartRef.current.x;
                      const dy = e.clientY - dragStartRef.current.y;
                      dragStartRef.current.x = e.clientX;
                      dragStartRef.current.y = e.clientY;
                      // Project movement onto the outward direction for this corner
                      const outward = dx * dragStartRef.current.outX + dy * dragStartRef.current.outY;
                      onSlotZoom(si, outward / dragStartRef.current.w);
                    }}
                    onPointerUp={(e) => { e.currentTarget.releasePointerCapture(e.pointerId); dragStartRef.current = null; }}
                  />
                ))}

                {/* ── Top-center handle: rotate ── */}
                {onSlotRotate && (
                  <Box
                    component="span"
                    title="Drag left/right to rotate"
                    sx={{
                      position: 'absolute', left: '50%', top: 0,
                      transform: 'translate(-50%, -50%)',
                      width: 18, height: 18, borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                      bgcolor: 'success.main', opacity: 0.9, zIndex: 100,
                      boxShadow: '0 1px 5px rgba(0,0,0,0.55)', border: '2px solid white',
                      cursor: 'ew-resize',
                      touchAction: 'none',
                    }}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture(e.pointerId);
                      const rect = e.currentTarget.parentElement?.getBoundingClientRect() || { width: 1 };
                      dragStartRef.current = { type: 'rotate', x: e.clientX, w: rect.width };
                    }}
                    onPointerMove={(e) => {
                      if (!dragStartRef.current || dragStartRef.current.type !== 'rotate') return;
                      const dx = e.clientX - dragStartRef.current.x;
                      dragStartRef.current.x = e.clientX;
                      // 1px → ~0.5° feels natural
                      onSlotRotate(si, dx * 0.5);
                    }}
                    onPointerUp={(e) => { e.currentTarget.releasePointerCapture(e.pointerId); dragStartRef.current = null; }}
                  />
                )}
              </>
            )}
          </Box>
        );
      })}

      {(page.textBlocks || []).map((tb, ti) => (
        <Box
          key={ti}
          sx={{
            position: 'absolute',
            left: Math.round(tb.x * SCALE),
            top: Math.round(tb.y * SCALE),
            width: Math.round(tb.width * SCALE),
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          <Typography
            sx={{
              fontSize: Math.round(tb.fontSize * SCALE * 0.6),
              fontWeight: tb.fontWeight || 'normal',
              color: tb.color || '#333',
              textAlign: tb.align || 'left',
              lineHeight: 1.2,
              textShadow: '0 1px 3px rgba(255,255,255,0.7)',
            }}
          >
            {tb.text}
          </Typography>
        </Box>
      ))}

      {pageMarginMm > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: marginPx,
            top: marginPx,
            width: PREVIEW_W_PX - marginPx * 2,
            height: PREVIEW_H_PX - marginPx * 2,
            border: '1px dashed rgba(30,100,255,0.40)',
            pointerEvents: 'none',
            zIndex: 50,
          }}
        />
      )}

      <Box sx={{ position: 'absolute', bottom: 4, right: 6, zIndex: 30, pointerEvents: 'none' }}>
        <Typography sx={{ fontSize: 9, color: 'rgba(0,0,0,0.3)', fontWeight: 500 }}>
          Page {(page.pageIndex ?? 0) + 1}
        </Typography>
      </Box>
    </Box>
  );
};

const ScaledPage = ({
  page,
  displayScale,
  pageMarginMm = 0,
  label,
  preloadedImageUrls = null,
  turnDirection = null,
  onSlotClick,
  onSlotDrag = null,
  onSlotZoom = null,
  onSlotRotate = null,
  selectedSlotIndex,
  onMoveBackward,
  onMoveForward,
  moveBackwardDisabled = false,
  moveForwardDisabled = false,
  onRemove = null,
  removeDisabled = false,
  onChangeLayout = null,
  onEditText = null,
  onMoveTo = null,
  onSplit = null,
}) => {
  const displayWidth = Math.round(PREVIEW_W_PX * displayScale);
  const displayHeight = Math.round(PREVIEW_H_PX * displayScale);

  return (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 0,
      flexShrink: 0,
      transformOrigin: turnDirection === 'prev' ? 'left center' : 'right center',
      animation: turnDirection ? `${turnDirection === 'prev' ? 'singlePageTurnPrev' : 'singlePageTurnNext'} 720ms cubic-bezier(0.2, 0.7, 0.2, 1)` : 'none',
      '@keyframes singlePageTurnNext': {
        '0%': {
          opacity: 0.62,
          transform: 'perspective(1800px) rotateY(-22deg) translateX(18px) scale(0.985)',
        },
        '45%': {
          opacity: 0.88,
          transform: 'perspective(1800px) rotateY(-8deg) translateX(8px) scale(0.992)',
        },
        '100%': {
          opacity: 1,
          transform: 'perspective(1800px) rotateY(0deg) translateX(0) scale(1)',
        },
      },
      '@keyframes singlePageTurnPrev': {
        '0%': {
          opacity: 0.62,
          transform: 'perspective(1800px) rotateY(22deg) translateX(-18px) scale(0.985)',
        },
        '45%': {
          opacity: 0.88,
          transform: 'perspective(1800px) rotateY(8deg) translateX(-8px) scale(0.992)',
        },
        '100%': {
          opacity: 1,
          transform: 'perspective(1800px) rotateY(0deg) translateX(0) scale(1)',
        },
      },
    }}
  >
    {(label || onMoveBackward || onMoveForward || onRemove || onChangeLayout || onEditText || onMoveTo || onSplit) && (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25, height: PAGE_LABEL_HEIGHT_PX, flexShrink: 0 }}>
        {onMoveBackward && (
          <Tooltip title="Move scene earlier">
            <span>
              <IconButton size="small" onClick={onMoveBackward} disabled={moveBackwardDisabled}>
                <NavigateBefore fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', minWidth: 54, textAlign: 'center' }}>
            {label}
          </Typography>
        )}
        {onMoveForward && (
          <Tooltip title="Move scene later">
            <span>
              <IconButton size="small" onClick={onMoveForward} disabled={moveForwardDisabled}>
                <NavigateNext fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onMoveTo && (
          <Tooltip title="Move to any position">
            <span>
              <IconButton size="small" onClick={onMoveTo}>
                <MoveToIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onEditText && (
          <Tooltip title="Edit text / scene name">
            <span>
              <IconButton size="small" onClick={onEditText}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onChangeLayout && (
          <Tooltip title="Change layout template">
            <span>
              <IconButton size="small" onClick={onChangeLayout}>
                <ChangeLayoutIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onSplit && (
          <Tooltip title="Split into separate scenes">
            <span>
              <IconButton size="small" onClick={onSplit} sx={{ color: 'warning.main' }}>
                <SplitIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {onRemove && (
          <Tooltip title="Remove this scene">
            <span>
              <IconButton size="small" onClick={onRemove} disabled={removeDisabled} sx={{ color: 'error.main' }}>
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    )}
    <Box sx={{ width: displayWidth, height: displayHeight, overflow: 'hidden', flexShrink: 0 }}>
      <Box sx={{ transform: `scale(${displayScale})`, transformOrigin: 'top left' }}>
        <PagePreview
          page={page}
          pageMarginMm={pageMarginMm}
          preloadedImageUrls={preloadedImageUrls}
          onSlotClick={onSlotClick}
          onSlotDrag={onSlotDrag}
          onSlotZoom={onSlotZoom}
          onSlotRotate={onSlotRotate}
          selectedSlotIndex={selectedSlotIndex}
        />
      </Box>
    </Box>
  </Box>
  );
};

const TurningPageOverlay = ({ frontPage, backPage, displayScale, pageMarginMm = 0, direction, side, hingeOffsetPx = 0, preloadedImageUrls = null, flipDurationMs = PAGE_TURN_DURATION_MS }) => {
  const displayWidth = Math.round(PREVIEW_W_PX * displayScale);
  const displayHeight = Math.round(PREVIEW_H_PX * displayScale);

  if (!frontPage || !direction) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: PAGE_LABEL_HEIGHT_PX,
        left: side === 'left' ? 0 : 'auto',
        right: side === 'right' ? 0 : 'auto',
        width: displayWidth,
        height: displayHeight,
        overflow: 'visible',
        pointerEvents: 'none',
        zIndex: 5,
        // NO perspective css-property here — having it on both this box AND in the
        // keyframe transform() creates a double-perspective that breaks backfaceVisibility
        // detection for children. The perspective() inside the keyframe handles visual depth.
        transformStyle: 'preserve-3d',
        willChange: 'transform',
        transformOrigin: direction === 'next'
          ? `${-hingeOffsetPx}px center`
          : `calc(100% + ${hingeOffsetPx}px) center`,
        animation: `${direction === 'next' ? 'overlayPageTurnNext' : 'overlayPageTurnPrev'} ${flipDurationMs}ms cubic-bezier(0.42, 0, 0.38, 1) forwards`,
        boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
        '@keyframes overlayPageTurnNext': {
          '0%':   { transform: 'perspective(8000px) rotateY(0deg)' },
          '100%': { transform: 'perspective(8000px) rotateY(-178deg)' },
        },
        '@keyframes overlayPageTurnPrev': {
          '0%':   { transform: 'perspective(8000px) rotateY(0deg)' },
          '100%': { transform: 'perspective(8000px) rotateY(178deg)' },
        },
      }}
    >
      {/*
        Inner preserve-3d container — NO overflow:hidden (that cancels preserve-3d per spec).
        The two face boxes each get an explicit 3D rotateY transform so that Chrome
        activates backfaceVisibility:hidden on them (Chrome ignores backfaceVisibility on
        elements without a 3D transform of their own).
        will-change:transform on each face creates a GPU composited layer, which forces the
        browser to rasterize and decode image content even before the face is visible.
      */}
      <Box sx={{ position: 'relative', width: displayWidth, height: displayHeight, transformStyle: 'preserve-3d' }}>

        {/* Front face — rotateY(0deg) activates backfaceVisibility in Chrome */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            transform: 'rotateY(0deg)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform',
          }}
        >
          <Box sx={{ width: displayWidth, height: displayHeight, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box sx={{ transform: `scale(${displayScale})`, transformOrigin: 'top left' }}>
              <PagePreview page={frontPage} pageMarginMm={pageMarginMm} preloadedImageUrls={preloadedImageUrls} />
            </Box>
          </Box>
        </Box>

        {/* Back face — pre-rotated 180° so it faces away at start; content counter-rotated to
            appear un-mirrored when the page has turned over */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            transform: 'rotateY(180deg)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform',
          }}
        >
          <Box sx={{ width: displayWidth, height: displayHeight, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <Box sx={{ transform: `scale(${displayScale})`, transformOrigin: 'top left' }}>
              <PagePreview page={backPage || frontPage} pageMarginMm={pageMarginMm} preloadedImageUrls={preloadedImageUrls} />
            </Box>
          </Box>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background: 'linear-gradient(90deg, rgba(0,0,0,0.16) 0%, rgba(210,192,160,0.12) 34%, rgba(210,192,160,0.08) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              width: 18,
              pointerEvents: 'none',
              background: 'linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.05) 70%, rgba(0,0,0,0) 100%)',
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

const EmptyPage = ({ displayScale, label = 'End of album' }) => {
  const displayWidth = Math.round(PREVIEW_W_PX * displayScale);
  const displayHeight = Math.round(PREVIEW_H_PX * displayScale);

  return (
  <Box
    sx={{
      width: displayWidth,
      height: displayHeight,
      border: '1px solid',
      borderColor: 'grey.300',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'grey.100',
      flexShrink: 0,
    }}
  >
    <Typography variant="caption" color="text.disabled">{label}</Typography>
  </Box>
  );
};

const WizardStep6Preview = ({ session, onNext, onBack, onNavConfigChange }) => {
  const sessionId = session?.uniqueId;

  const [loading, setLoading] = useState(true);
  const [failedScenes, setFailedScenes] = useState([]);

    // Signal nav config to parent on mount: back + Finish button
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
      onNavConfigChange?.({
        nextLabel: 'Finish Album',
        nextDisabled: false,
        backDisabled: false,
        showSkip: false,
      });
    }, []); // fires once on mount
  const [error, setError] = useState(null);
  const [albumLayout, setAlbumLayout] = useState(null);
  const [draftLayouts, setDraftLayouts] = useState([]);
  const [spreadIndex, setSpreadIndex] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [minRatingFilter, setMinRatingFilter] = useState(0); // 0 = show all
  const [editMessage, setEditMessage] = useState(null);
  const [sessionImages, setSessionImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [albumHighlights, setAlbumHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSlotOriginal, setSelectedSlotOriginal] = useState(null);
  const [selectedReplacementImageId, setSelectedReplacementImageId] = useState('');
  const [previewAreaWidth, setPreviewAreaWidth] = useState(0);
  const [pageTurnState, setPageTurnState] = useState(null);
  const [isPreparingTurn, setIsPreparingTurn] = useState(false);
  const [preloadedImageUrlById, setPreloadedImageUrlById] = useState({});
  const [flipDurationMs, setFlipDurationMs] = useState(PAGE_TURN_DURATION_MS);
  const [swapImagesPage, setSwapImagesPage] = useState(0);
  const [swapImagesPageSize, setSwapImagesPageSize] = useState(50);
  const [highlightsPage, setHighlightsPage] = useState(0);
  const [highlightsPageSize, setHighlightsPageSize] = useState(50);
  // ── Dialog / action state ──
  const [storyScenes, setStoryScenes] = useState([]);
  const [albumContext, setAlbumContext] = useState('');
  const [albumDataImages, setAlbumDataImages] = useState([]);
  const [changeLayoutDialogOpen, setChangeLayoutDialogOpen] = useState(false);
  const [changeLayoutTarget, setChangeLayoutTarget] = useState(null); // { layoutIndex, pageArrayIndex }
  const [editTextDialogOpen, setEditTextDialogOpen] = useState(false);
  const [editTextTarget, setEditTextTarget] = useState(null); // { layoutIndex, pageArrayIndex }
  const [editTextValues, setEditTextValues] = useState({ sceneName: '', textBlocks: [] });
  const [moveToDialogOpen, setMoveToDialogOpen] = useState(false);
  const [moveToTargetLayout, setMoveToTargetLayout] = useState(null);
  const [moveToPosition, setMoveToPosition] = useState(1);
  const [moveSpreadDialogOpen, setMoveSpreadDialogOpen] = useState(false);
  const [moveSpreadPosition, setMoveSpreadPosition] = useState(1);
  const [imageDetailDialog, setImageDetailDialog] = useState({ open: false, imageId: null });
  const [changeLayoutWizard, setChangeLayoutWizard] = useState(null);
  const [changeLayoutTab, setChangeLayoutTab] = useState(0);
  const [dbAlbumTemplateData, setDbAlbumTemplateData] = useState([]); // raw data from GET /api/album/templates
  const [albumPlan, setAlbumPlan] = useState(null);
  const socketRef = useRef(null);
  const previewAreaRef = useRef(null);
  const pageTurnTimeoutRef = useRef(null);
  const navigationRequestRef = useRef(0);
  const preloadedImageUrlByIdRef = useRef({});
  const flipDurationMsRef = useRef(PAGE_TURN_DURATION_MS);
  const carouselRef = useRef(null);

  useEffect(() => {
    preloadedImageUrlByIdRef.current = preloadedImageUrlById;
  }, [preloadedImageUrlById]);

  useEffect(() => {
    flipDurationMsRef.current = flipDurationMs;
  }, [flipDurationMs]);

  // Deselect slot controls when spread changes
  useEffect(() => {
    setSelectedSlot(null);
    setSelectedSlotOriginal(null);
  }, [spreadIndex]);

  // Keep the active scene card centred in the carousel
  useEffect(() => {
    if (!carouselRef.current) return;
    const active = carouselRef.current.querySelector('[data-carousel-active="true"]');
    if (active) active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [spreadIndex]);

  const loadLayout = useCallback((silent = false) => {
    if (!sessionId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);

    albumService.getAlbumLayout(sessionId)
      .then((data) => {
        const nextAlbumLayout = data.albumLayout || null;
        setAlbumLayout(nextAlbumLayout);
        setFailedScenes(nextAlbumLayout?.failedScenes || []);
        // Normalize cross-page hero templates: ensure mandatory zoom/focus values.
        // PanoramaSpread_1: zoom=2.0 on both pages.
        // CrossHero: page 0 zoom=1.67/focusX=0.30, page 1 hero-cont zoom=2.50/focusX=0.80.
        const rawLayouts = nextAlbumLayout?.layouts || [];
        const normalizedLayouts = rawLayouts.map(layout => {
          if (layout.templatePairName === 'PanoramaSpread_1') {
            return {
              ...layout,
              pages: (layout.pages || []).map(page => ({
                ...page,
                images: (page.images || []).map(img =>
                  (img.zoom == null || img.zoom <= 1.05) ? { ...img, zoom: 2.0 } : img
                ),
              })),
            };
          }
          if (layout.templatePairName === 'CrossHero_Portrait' || layout.templatePairName === 'CrossHero_1') {
            return {
              ...layout,
              pages: (layout.pages || []).map((page, pi) => ({
                ...page,
                images: (page.images || []).map((img, ii) => {
                  if (pi === 0 && (img.zoom == null || img.zoom <= 1.05)) return { ...img, zoom: 1.67, focusX: img.focusX ?? 0.30 };
                  if (pi === 1 && ii === 0 && (img.zoom == null || img.zoom <= 1.05)) return { ...img, zoom: 2.50, focusX: img.focusX ?? 0.80 };
                  return img;
                }),
              })),
            };
          }
          return layout;
        });
        setDraftLayouts(normalizedLayouts);
        setHasDraftChanges(false);
        setSelectedSlot(null);
        setSelectedReplacementImageId('');
      })
      .catch((err) => setError(err.message || 'Failed to load layout'))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, [sessionId]);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  // Fetch all album template collections from the DB for the Change Layout dialog.
  useEffect(() => {
    albumService.getAlbumTemplates()
      .then((data) => { setDbAlbumTemplateData(data.templates || []); })
      .catch((err) => { console.warn('[ChangeLayout] Failed to load DB templates:', err.message); });
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    const loadSessionImages = async () => {
      setImagesLoading(true);
      try {
        const all = [];
        let page = 1;
        let hasNext = true;

        while (hasNext) {
          const resp = await sessionService.getSessionImages(sessionId, {
            page,
            limit: 200,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            mediaType: 'image',
          });

          all.push(...(resp.images || []));
          hasNext = Boolean(resp.pagination?.hasNext);
          page += 1;

          // Safety guard against accidental infinite pagination loops.
          if (page > 100) break;
        }

        const unique = Array.from(new Map(all.map((img) => [img.uniqueId, img])).values());
        setSessionImages(unique);
      } catch (err) {
        console.error('[Step7] Failed to load session images', err);
      } finally {
        setImagesLoading(false);
      }
    };
    loadSessionImages();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;
    const loadHighlights = async () => {
      setHighlightsLoading(true);
      try {
        const [timelineResp, albumDataResp] = await Promise.all([
          albumService.getStoryTimeline(sessionId),
          albumService.getAlbumData(sessionId),
        ]);
        const topImages = timelineResp?.storyTimeline?.topImages;
        const scenes = timelineResp?.storyTimeline?.scenes;
        const savedContext = timelineResp?.storyTimeline?.storySettings?.userContext;
        const enrichedImages = albumDataResp?.images;
        if (!cancelled) {
          setAlbumHighlights(Array.isArray(topImages) ? topImages : []);
          setStoryScenes(Array.isArray(scenes) ? scenes : []);
          setAlbumContext(typeof savedContext === 'string' ? savedContext.trim() : '');
          setAlbumDataImages(Array.isArray(enrichedImages) ? enrichedImages : []);
        }
        // Load plan independently (non-blocking)
        albumService.getAlbumPlan(sessionId)
          .then(data => { if (data?.albumPlan && !cancelled) setAlbumPlan(data.albumPlan); })
          .catch(() => {});
      } catch (err) {
        console.error('[Step7] Failed to load story highlights', err);
        if (!cancelled) {
          setAlbumHighlights([]);
          setStoryScenes([]);
          setAlbumContext('');
          setAlbumDataImages([]);
        }
      } finally {
        if (!cancelled) setHighlightsLoading(false);
      }
    };

    loadHighlights();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const socket = io(API_URL, { path: '/socket.io/', withCredentials: true });
    socket.on('connect', () => socket.emit('joinLayoutRoom', { sessionId }));
    socket.on('layoutComplete', (data) => { if (data.sessionId === sessionId) loadLayout(true); });
    socket.on('layoutCancelled', (data) => { if (data.sessionId === sessionId) loadLayout(true); });
    socketRef.current = socket;
    return () => { socket.disconnect(); socketRef.current = null; };
  }, [loadLayout, sessionId]);

  const allPages = useMemo(() => {
    if (!draftLayouts?.length) return [];
    const pages = [];
    draftLayouts.forEach((layout, layoutIndex) => {
      const sortedPages = [...(layout.pages || [])]
        .map((page, pageArrayIndex) => ({ page, pageArrayIndex }))
        .sort((a, b) => (a.page.pageIndex ?? 0) - (b.page.pageIndex ?? 0));

      sortedPages.forEach(({ page, pageArrayIndex }) => {
        pages.push({
          page,
          sceneName: layout.sceneName || layout.sceneId || 'Scene',
          // Prefer per-page sceneTitle (set per page by the backend) so that continuation
          // spreads show the title the model chose for that specific spread, not the first
          // spread's title which is stored at the layout level.
          sceneTitle: page.sceneTitle || layout.sceneTitle || layout.sceneName || layout.sceneId || '',
          // Prefer per-page sub-template (set by backend when the layout uses a compound
          // templatePairName like "HeroLeft_Grid3x3Right|SoloHero_1") so the chip only
          // shows the template relevant to this specific page, not future spread pages.
          templatePairName: page.singlePageTemplate || layout.templatePairName || '',
          layoutIndex,
          pageArrayIndex,
        });
      });
    });
    return pages;
  }, [draftLayouts]);

  const storySceneById = useMemo(
    () => new Map(storyScenes.map(s => [s.sceneId, s])),
    [storyScenes]
  );

  // Album plan lookups for the Jump-to-scene sidebar
  const planEntryBySceneId = useMemo(
    () => new Map((albumPlan?.scene_plan || []).map(p => [p.sceneId, p])),
    [albumPlan]
  );

  // Compute which physical spread each scene starts on (based on plan budgets)
  const planSpreadStartBySceneId = useMemo(() => {
    const map = new Map();
    let page = 1;
    for (const entry of (albumPlan?.scene_plan || [])) {
      const budget = Math.max(1, entry.budget || 1);
      // virtual pages: [null(cover), p1, p2, …]; spread = floor(virtualIndex / 2)
      const virtualIndex = page; // page 1 is at virtualIndex 1
      const spreadStart = Math.floor(virtualIndex / 2);
      map.set(entry.sceneId, spreadStart);
      page += budget;
    }
    return map;
  }, [albumPlan]);

  const filteredPages = useMemo(() => {
    if (minRatingFilter === 0) return allPages;
    return allPages.filter(entry => {
      const layout = draftLayouts[entry.layoutIndex];
      const scene = storySceneById.get(layout?.sceneId);
      return (scene?.themeRelevanceScore ?? 0) >= minRatingFilter;
    });
  }, [allPages, draftLayouts, minRatingFilter, storySceneById]);

  const hiddenLayoutCount = useMemo(() => {
    if (minRatingFilter === 0) return 0;
    return draftLayouts.filter(layout => {
      const scene = storySceneById.get(layout.sceneId);
      return (scene?.themeRelevanceScore ?? 0) < minRatingFilter;
    }).length;
  }, [draftLayouts, minRatingFilter, storySceneById]);

  // Prepend a null (blank cover) so page 1 always opens on the right.
  // Pad to even length so spreads always pair cleanly; the trailing null is the back cover.
  const virtualPages = useMemo(() => {
    const vp = [null, ...filteredPages];
    if (vp.length % 2 !== 0) vp.push(null);
    return vp;
  }, [filteredPages]);

  const totalSpreads = virtualPages.length / 2;

  const imageUsageById = useMemo(() => {
    const usage = new Map();
    allPages.forEach((entry, pageIdx) => {
      (entry.page.images || []).forEach((slot) => {
        const imageId = slot?._meta?.imageId || slot?.imageId;
        const imageKey = imageId ? String(imageId) : '';
        if (!imageKey) return;
        if (!usage.has(imageKey)) {
          usage.set(imageKey, { pages: [] });
        }
        usage.get(imageKey).pages.push(pageIdx + 1);
      });
    });

    usage.forEach((value) => {
      value.pages = Array.from(new Set(value.pages)).sort((a, b) => a - b);
    });

    return usage;
  }, [allPages]);

  const sessionImageById = useMemo(
    () => new Map(sessionImages.map((img) => [String(img.uniqueId), img])),
    [sessionImages]
  );

  const sessionUsageStats = useMemo(() => {
    const total = sessionImages.length;
    const used = sessionImages.reduce((count, img) => {
      return imageUsageById.has(String(img.uniqueId)) ? count + 1 : count;
    }, 0);
    return { used, total };
  }, [sessionImages, imageUsageById]);

  const highlightUsageStats = useMemo(() => {
    const total = albumHighlights.length;
    const used = albumHighlights.reduce((count, highlight) => {
      const imageId = String(highlight?.imageId || '');
      return imageId && imageUsageById.has(imageId) ? count + 1 : count;
    }, 0);
    return { used, total };
  }, [albumHighlights, imageUsageById]);

  // Unique layout templates derived from current draftLayouts (by templatePairName)
  const uniqueTemplates = useMemo(() => {
    const seen = new Set();
    return draftLayouts.filter((l) => {
      if (!l.templatePairName || seen.has(l.templatePairName)) return false;
      seen.add(l.templatePairName);
      return true;
    });
  }, [draftLayouts]);

  // Per-page templates derived from the current album (deduplicated by slot geometry fingerprint)
  const uniquePageTemplates = useMemo(() => {
    const seen = new Set();
    const result = [];
    draftLayouts.forEach((layout) => {
      (layout.pages || []).forEach((page) => {
        const slots = (page.images || []).map(({ x, y, width, height, name }) => ({ x: Math.round(x), y: Math.round(y), width: Math.round(width), height: Math.round(height), name }));
        if (slots.length === 0) return;
        const fp = slots.map((s) => `${s.x},${s.y},${s.width},${s.height}`).join('|');
        if (seen.has(fp)) return;
        seen.add(fp);
        result.push({ id: fp, name: layout.templatePairName || `${slots.length}-slot`, description: `From: ${layout.sceneName || 'scene'}`, slotCount: slots.length, slots, category: 'album' });
      });
    });
    return result;
  }, [draftLayouts]);

  // Flat list of per-page templates derived from the DB album template collections.
  // Each pageTemplate.pages[i] becomes one selectable dialog item.
  const dbPageTemplates = useMemo(() => {
    const titleStripMm = albumLayout?.showTitleStrip ? 16 : 0;
    const result = [];
    dbAlbumTemplateData.forEach((albumTmpl) => {
      (albumTmpl.pageTemplates || []).forEach((pt) => {
        const pages = pt.pages || [];
        pages.forEach((p) => {
          if (!p.zones?.length) return;
          const slots = p.zones.map((z) => zoneToSlot(z, titleStripMm));
          const slotCount = slots.length;
          const multiPage = pages.length > 1;
          result.push({
            id:          `${pt.name}_p${p.pageIndex}`,
            name:        multiPage ? `${pt.name} (p${p.pageIndex + 1})` : pt.name,
            description: pt.description || '',
            slotCount,
            slots,
            category:    slotCountToCategory(slotCount),
          });
        });
      });
    });
    return result;
  }, [dbAlbumTemplateData, albumLayout]);


  useEffect(() => {
    if (totalSpreads === 0) return;
    setSpreadIndex((prev) => Math.min(prev, totalSpreads - 1));
  }, [totalSpreads]);

  const sourceSpreadIndex = pageTurnState ? pageTurnState.fromSpreadIndex : spreadIndex;
  const targetSpreadIndex = pageTurnState ? pageTurnState.toSpreadIndex : spreadIndex;
  const sourceSpreadEntries = {
    left: virtualPages[sourceSpreadIndex * 2] ?? null,
    right: virtualPages[sourceSpreadIndex * 2 + 1] ?? null,
  };
  const targetSpreadEntries = {
    left: virtualPages[targetSpreadIndex * 2] ?? null,
    right: virtualPages[targetSpreadIndex * 2 + 1] ?? null,
  };

  const leftEntry = pageTurnState?.direction === 'next'
    ? sourceSpreadEntries.left
    : targetSpreadEntries.left;
  const rightEntry = pageTurnState?.direction === 'next'
    ? targetSpreadEntries.right
    : sourceSpreadEntries.right;

  const visibleSpreadIndex = pageTurnState?.direction === 'next'
    ? pageTurnState.fromSpreadIndex
    : spreadIndex;
  const spreadSceneName = leftEntry?.sceneName ?? '';
  // Prefer page-level sceneTitle (set per continuation-spread by the backend) so that
  // e.g. "Trafalgar Square Epilogue" shows on page 12, not "Trafalgar Square at Dusk".
  const spreadSceneTitle = leftEntry ? (leftEntry.sceneTitle || draftLayouts[leftEntry.layoutIndex]?.sceneTitle || leftEntry.sceneName || '') : '';
  const rightSceneTitle = (rightEntry && rightEntry.layoutIndex !== leftEntry?.layoutIndex)
    ? (rightEntry.sceneTitle || draftLayouts[rightEntry.layoutIndex]?.sceneTitle || rightEntry.sceneName || '')
    : '';
  const spreadTemplate = leftEntry?.templatePairName ?? '';
  // Show a separate right-page chip when the right page belongs to a different scene (layoutIndex)
  // OR to the same scene but a different per-page sub-template (e.g. page 11=TrioPortrait vs page 12=EightGrid).
  const rightTemplate = (rightEntry &&
    (rightEntry.layoutIndex !== leftEntry?.layoutIndex ||
     (rightEntry.templatePairName && rightEntry.templatePairName !== leftEntry?.templatePairName)))
    ? (rightEntry.templatePairName ?? '')
    : '';
  const leftSceneRating = useMemo(() => {
    if (!leftEntry) return null;
    const sceneId = draftLayouts[leftEntry.layoutIndex]?.sceneId;
    if (!sceneId) return null;
    const score = storySceneById.get(sceneId)?.themeRelevanceScore;
    return score == null ? null : { sceneName: leftEntry.sceneName, score };
  }, [leftEntry, draftLayouts, storySceneById]);
  const rightSceneRating = useMemo(() => {
    if (!rightEntry) return null;
    const sceneId = draftLayouts[rightEntry.layoutIndex]?.sceneId;
    if (!sceneId) return null;
    const score = storySceneById.get(sceneId)?.themeRelevanceScore;
    return score == null ? null : { sceneName: rightEntry.sceneName, score };
  }, [rightEntry, draftLayouts, storySceneById]);
  // Virtual page 0 = blank cover; content pages start at virtual index 1.
  // leftPageNum/rightPageNum count only content pages (1-based).
  const leftPageNum = visibleSpreadIndex * 2;          // 0 on spread 0 (cover)
  const rightPageNum = Math.min(visibleSpreadIndex * 2 + 1, filteredPages.length);
  const visiblePageCount = rightEntry ? 2 : leftEntry ? 1 : 0;
  const responsiveDisplayScale = useMemo(() => {
    if (!previewAreaWidth || visiblePageCount === 0) return DEFAULT_DISPLAY_SCALE;
    const availableWidth = Math.max(0, previewAreaWidth - (PREVIEW_GAP_PX * Math.max(0, visiblePageCount - 1)));
    const fitScale = availableWidth / (visiblePageCount * PREVIEW_W_PX);
    return Math.max(0.1, fitScale);
  }, [previewAreaWidth, visiblePageCount]);
  const selectedSlotData = useMemo(() => {
    if (!selectedSlot) return null;
    return draftLayouts[selectedSlot.layoutIndex]
      ?.pages?.[selectedSlot.pageArrayIndex]
      ?.images?.[selectedSlot.slotIndex] || null;
  }, [draftLayouts, selectedSlot]);

  useEffect(() => {
    const element = previewAreaRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setPreviewAreaWidth(element.clientWidth || 0);
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => () => {
    if (pageTurnTimeoutRef.current) {
      clearTimeout(pageTurnTimeoutRef.current);
    }
    Object.values(preloadedImageUrlByIdRef.current || {}).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (err) {
        // Best-effort cleanup only.
      }
    });
  }, []);

  const getSpreadEntries = useCallback((index) => {
    const safeIndex = Math.max(0, index);
    return {
      left: virtualPages[safeIndex * 2] ?? null,
      right: virtualPages[safeIndex * 2 + 1] ?? null,
    };
  }, [virtualPages]);

  const preloadSpreadImages = useCallback(async (spreadEntries) => {
    const imageIds = new Set();
    [spreadEntries?.left, spreadEntries?.right].forEach((entry) => {
      (entry?.page?.images || []).forEach((slot) => {
        const imageId = slot?._meta?.imageId || slot?.imageId;
        if (imageId) imageIds.add(String(imageId));
      });
    });

    // Use ref so this function stays stable and doesn't get a stale cache view
    const idsToLoad = Array.from(imageIds).filter((id) => !preloadedImageUrlByIdRef.current[id]);
    if (!idsToLoad.length) return;

    const loaded = await Promise.all(
      idsToLoad.map(async (id) => [id, await loadAuthenticatedImage(id, 'serve')])
    );

    setPreloadedImageUrlById((prev) => {
      const next = { ...prev };
      loaded.forEach(([id, url]) => {
        if (url && !next[id]) {
          next[id] = url;
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // stable — uses ref for cache check, functional setter for update

  // Preload spreads 0, 1, 2 as soon as the layout data arrives
  useEffect(() => {
    if (!allPages.length || !totalSpreads) return;
    [0, 1, 2].forEach((idx) => {
      if (idx < totalSpreads) preloadSpreadImages(getSpreadEntries(idx));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPages.length]); // intentionally only fires when page count changes (layout load)

  // Background-preload adjacent spreads whenever the user lands on a new spread
  useEffect(() => {
    if (!allPages.length || !totalSpreads) return;
    [spreadIndex - 2, spreadIndex - 1, spreadIndex + 1, spreadIndex + 2].forEach((idx) => {
      if (idx >= 0 && idx < totalSpreads) preloadSpreadImages(getSpreadEntries(idx));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadIndex]); // intentionally omits callbacks — they are stable after above fix

  const goToSpread = useCallback(async (targetIndex) => {
    if (pageTurnState || isPreparingTurn) return;

    const clampedTarget = Math.max(0, Math.min(totalSpreads - 1, targetIndex));
    if (clampedTarget === spreadIndex) return;

    if (pageTurnTimeoutRef.current) {
      clearTimeout(pageTurnTimeoutRef.current);
      pageTurnTimeoutRef.current = null;
    }

    if (Math.abs(clampedTarget - spreadIndex) === 1) {
      const requestId = navigationRequestRef.current + 1;
      navigationRequestRef.current = requestId;
      const spinnerStartedAt = Date.now();
      setIsPreparingTurn(true);
      try {
        await waitForNextPaint();
        await Promise.all([
          preloadSpreadImages(getSpreadEntries(spreadIndex)),
          preloadSpreadImages(getSpreadEntries(clampedTarget)),
        ]);
        const remainingSpinnerMs = PRE_TURN_SPINNER_MIN_MS - (Date.now() - spinnerStartedAt);
        if (remainingSpinnerMs > 0) {
          await new Promise((resolve) => setTimeout(resolve, remainingSpinnerMs));
        }
      } finally {
        if (navigationRequestRef.current === requestId) {
          setIsPreparingTurn(false);
        }
      }
      if (requestId !== navigationRequestRef.current) return;

      setPageTurnState({
        direction: clampedTarget > spreadIndex ? 'next' : 'prev',
        fromSpreadIndex: spreadIndex,
        toSpreadIndex: clampedTarget,
      });
      pageTurnTimeoutRef.current = setTimeout(() => {
        setSpreadIndex(clampedTarget);
        setPageTurnState(null);
        pageTurnTimeoutRef.current = null;
      }, flipDurationMsRef.current + 20);
    } else {
      setIsPreparingTurn(false);
      setPageTurnState(null);
      setSpreadIndex(clampedTarget);
    }
  }, [getSpreadEntries, isPreparingTurn, pageTurnState, preloadSpreadImages, spreadIndex, totalSpreads]);

  const handlePrev = useCallback(() => {
    goToSpread(spreadIndex - 1);
  }, [goToSpread, spreadIndex]);

  const handleNext = useCallback(() => {
    goToSpread(spreadIndex + 1);
  }, [goToSpread, spreadIndex]);

  const isAnyDialogOpen =
    changeLayoutDialogOpen ||
    editTextDialogOpen ||
    moveToDialogOpen ||
    moveSpreadDialogOpen ||
    imageDetailDialog.open ||
    Boolean(changeLayoutWizard);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (isAnyDialogOpen || isPreparingTurn || pageTurnState) return;
      if (isEditableTarget(event.target)) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, isAnyDialogOpen, isPreparingTurn, pageTurnState]);

  const moveLayoutByOffset = useCallback((layoutIndex, offset) => {
    const targetIndex = layoutIndex + offset;
    if (layoutIndex < 0 || targetIndex < 0 || targetIndex >= draftLayouts.length) return;

    let nextSpreadIndex = 0;
    setDraftLayouts((prev) => {
      const next = [...prev];
      const temp = next[layoutIndex];
      next[layoutIndex] = next[targetIndex];
      next[targetIndex] = temp;

      let pageCountBefore = 0;
      for (let i = 0; i < targetIndex; i += 1) {
        pageCountBefore += next[i]?.pages?.length ?? 0;
      }
      nextSpreadIndex = Math.floor(pageCountBefore / 2);
      return next;
    });

    setSpreadIndex(nextSpreadIndex);
    setSelectedSlot(null);
    setSelectedReplacementImageId('');
    setHasDraftChanges(true);
    setEditMessage({ severity: 'success', text: `Moved scene ${offset < 0 ? 'earlier' : 'later'} in the album.` });
  }, [draftLayouts.length]);

  const removeLayout = useCallback((layoutIndex) => {
    if (draftLayouts.length <= 1) {
      setEditMessage({ severity: 'warning', text: 'Cannot remove the only remaining scene.' });
      return;
    }
    setDraftLayouts((prev) => {
      const next = [...prev];
      next.splice(layoutIndex, 1);
      return next;
    });
    setSpreadIndex((s) => Math.max(0, s - 1));
    setSelectedSlot(null);
    setSelectedReplacementImageId('');
    setHasDraftChanges(true);
    setEditMessage({ severity: 'success', text: 'Scene removed.' });
  }, [draftLayouts.length]);

  const moveLayoutToIndex = useCallback((fromIndex, toIndex) => {
    const clampedTo = Math.max(0, Math.min(draftLayouts.length - 1, toIndex));
    if (fromIndex === clampedTo) return;
    let nextSpreadIndex = 0;
    setDraftLayouts((prev) => {
      const next = [...prev];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(clampedTo, 0, removed);
      let pageCountBefore = 0;
      for (let i = 0; i < clampedTo; i++) pageCountBefore += next[i]?.pages?.length ?? 0;
      nextSpreadIndex = Math.floor(pageCountBefore / 2);
      return next;
    });
    setSpreadIndex(nextSpreadIndex);
    setSelectedSlot(null);
    setSelectedReplacementImageId('');
    setHasDraftChanges(true);
    setMoveToDialogOpen(false);
    setEditMessage({ severity: 'success', text: 'Scene moved to new position.' });
  }, [draftLayouts.length]);

  const splitScene = useCallback((layoutIndex) => {
    setDraftLayouts((prev) => {
      const scene = prev[layoutIndex];
      if (!scene || (scene.pages?.length ?? 0) <= 1) return prev;
      const newEntries = (scene.pages || []).map((page, pi) => ({
        ...scene,
        pages: [page],
        sceneId: pi === 0 ? scene.sceneId : `${scene.sceneId || layoutIndex}_split${pi}`,
        sceneName: pi === 0 ? scene.sceneName : (scene.sceneName ? `${scene.sceneName} (${pi + 1})` : null),
      }));
      const next = [...prev];
      next.splice(layoutIndex, 1, ...newEntries);
      return next;
    });
    setHasDraftChanges(true);
    setEditMessage({ severity: 'success', text: 'Scene split into separate pages.' });
  }, []);

  const moveSpreadToIndex = useCallback((leftLayoutIdx, rightLayoutIdx, targetSpreadIdx) => {
    if (leftLayoutIdx == null && rightLayoutIdx == null) return;
    const uniqueIndices = [...new Set([leftLayoutIdx, rightLayoutIdx].filter((i) => i != null))].sort((a, b) => a - b);
    let nextSpreadIdx = targetSpreadIdx;
    setDraftLayouts((prev) => {
      const next = [...prev];
      // Remove from back to front to keep earlier indices stable
      const removed = [];
      [...uniqueIndices].reverse().forEach((idx) => { removed.unshift(...next.splice(idx, 1)); });
      // Find insertion point in the reduced array
      let pageCount = 0;
      let insertIdx = next.length;
      for (let i = 0; i < next.length; i++) {
        if (Math.floor(pageCount / 2) >= targetSpreadIdx) { insertIdx = i; break; }
        pageCount += next[i]?.pages?.length ?? 0;
      }
      removed.forEach((item, offset) => next.splice(insertIdx + offset, 0, item));
      // Recalculate actual spread index after insertion
      pageCount = 0;
      for (let i = 0; i < insertIdx; i++) pageCount += next[i]?.pages?.length ?? 0;
      nextSpreadIdx = Math.floor(pageCount / 2);
      return next;
    });
    setSpreadIndex(nextSpreadIdx);
    setSelectedSlot(null);
    setSelectedReplacementImageId('');
    setHasDraftChanges(true);
    setMoveSpreadDialogOpen(false);
    setEditMessage({ severity: 'success', text: 'Spread moved to new position.' });
  }, []);

  // Collect candidate images from adjacent pages/scenes for the "add images" wizard step
  const getCandidateImages = useCallback((layoutIndex, pageArrayIndex) => {
    const seen = new Set();
    const result = [];
    const add = (imageId, _meta) => {
      if (!imageId || seen.has(String(imageId))) return;
      seen.add(String(imageId));
      result.push({ imageId: String(imageId), _meta: _meta || {} });
    };

    // 1. Find story scene for this layout → get its cluster IDs
    const sceneId = draftLayouts[layoutIndex]?.sceneId;
    const storyScene = sceneId ? storyScenes.find((s) => s.sceneId === sceneId) : null;
    const clusterIds = new Set((storyScene?.suggestedClusterIds || []).map(String));

    // 2. Images from the same story clusters (albumDataImages has clusterId per image)
    if (clusterIds.size > 0) {
      albumDataImages
        .filter((img) => clusterIds.has(String(img.clusterId)))
        .forEach((img) => add(img.id, {}));
    }

    // 3. Fallback: same scene other pages + adjacent scenes
    (draftLayouts[layoutIndex]?.pages || []).forEach((page, pgIdx) => {
      if (pgIdx === pageArrayIndex) return;
      (page.images || []).forEach((slot) => add(slot._meta?.imageId || slot.imageId, slot._meta));
    });
    if (layoutIndex > 0) {
      (draftLayouts[layoutIndex - 1]?.pages || []).forEach((page) =>
        (page.images || []).forEach((slot) => add(slot._meta?.imageId || slot.imageId, slot._meta))
      );
    }
    if (layoutIndex < draftLayouts.length - 1) {
      (draftLayouts[layoutIndex + 1]?.pages || []).forEach((page) =>
        (page.images || []).forEach((slot) => add(slot._meta?.imageId || slot.imageId, slot._meta))
      );
    }
    return result;
  }, [draftLayouts, storyScenes, albumDataImages]);

  const changePageLayout = useCallback((target, templateSlots, imageOverrides = null) => {
    if (!target || !templateSlots?.length) return;
    const { layoutIndex, pageArrayIndex } = target;
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const currentScene = next[layoutIndex];
      if (!currentScene) return prev;
      const currentPage = currentScene.pages?.[pageArrayIndex];
      if (!currentPage) return prev;
      const existingImages = imageOverrides ?? (currentPage.images || [])
        .map((slot) => { const imgId = slot._meta?.imageId || slot.imageId; return imgId ? { imageId: String(imgId), _meta: slot._meta } : null; })
        .filter(Boolean);
      const newImages = templateSlots.map((tSlot, idx) => {
        const base = { x: tSlot.x, y: tSlot.y, width: tSlot.width, height: tSlot.height, zoom: 1, focusX: 0.5, focusY: 0.5, rotate: 0 };
        if (tSlot.name) base.name = tSlot.name;
        const src = existingImages[idx];
        if (src) { base.imageId = src.imageId; base._meta = { ...(src._meta || {}), imageId: src.imageId }; }
        return base;
      });
      next[layoutIndex].pages[pageArrayIndex] = { ...currentPage, images: newImages };
      return next;
    });
    setSelectedSlot(null);
    setHasDraftChanges(true);
    setChangeLayoutDialogOpen(false);
    setChangeLayoutTarget(null);
    setChangeLayoutWizard(null);
    setEditMessage({ severity: 'success', text: 'Page layout changed.' });
  }, []);

  const openImageDetail = useCallback((imageId) => {
    if (!imageId) return;
    setImageDetailDialog({ open: true, imageId });
  }, []);

  const openEditText = useCallback((entry) => {
    if (!entry) return;
    const layout = draftLayouts[entry.layoutIndex];
    const page = layout?.pages?.[entry.pageArrayIndex];
    setEditTextTarget({ layoutIndex: entry.layoutIndex, pageArrayIndex: entry.pageArrayIndex });
    setEditTextValues({
      sceneName: layout?.sceneName || '',
      textBlocks: (page?.textBlocks || []).map((tb, i) => ({ index: i, text: tb.text || '' })),
    });
    setEditTextDialogOpen(true);
  }, [draftLayouts]);

  const saveEditText = useCallback(() => {
    if (!editTextTarget) return;
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      if (next[editTextTarget.layoutIndex]) {
        next[editTextTarget.layoutIndex].sceneName = editTextValues.sceneName;
        const page = next[editTextTarget.layoutIndex].pages?.[editTextTarget.pageArrayIndex];
        if (page) {
          editTextValues.textBlocks.forEach(({ index, text }) => {
            if (page.textBlocks?.[index] != null) page.textBlocks[index].text = text;
          });
        }
      }
      return next;
    });
    setHasDraftChanges(true);
    setEditTextDialogOpen(false);
    setEditMessage({ severity: 'success', text: 'Text updated.' });
  }, [editTextTarget, editTextValues]);

  const handleSelectSlot = useCallback((entry, slotIndex) => {
    const slot = entry?.page?.images?.[slotIndex];
    if (!slot) return;
    const imageId = slot._meta?.imageId || slot.imageId || '';
    setSelectedSlot({
      layoutIndex: entry.layoutIndex,
      pageArrayIndex: entry.pageArrayIndex,
      slotIndex,
      imageId,
    });
    setSelectedSlotOriginal({
      zoom: slot.zoom ?? 1,
      focusX: slot.focusX ?? 0.5,
      focusY: slot.focusY ?? 0.5,
      rotate: slot.rotate ?? 0,
      filters: slot.filters ? { ...slot.filters } : {},
    });
    setSelectedReplacementImageId(imageId);
  }, []);

  const updateSelectedSlotTransform = useCallback((updates) => {
    if (!selectedSlot) return;
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const slot = next[selectedSlot.layoutIndex]?.pages?.[selectedSlot.pageArrayIndex]?.images?.[selectedSlot.slotIndex];
      if (!slot) return prev;

      if (updates.zoom != null) {
        slot.zoom = clamp(Number(updates.zoom), 0.1, 5);
      }
      if (updates.rotate != null) {
        slot.rotate = clamp(Number(updates.rotate), -180, 180);
      }
      if (updates.focusX != null) {
        slot.focusX = clamp(Number(updates.focusX), 0, 1);
      }
      if (updates.focusY != null) {
        slot.focusY = clamp(Number(updates.focusY), 0, 1);
      }
      if (updates.filters != null) {
        slot.filters = { ...(slot.filters || {}), ...updates.filters };
      }

      return next;
    });

    setHasDraftChanges(true);
  }, [selectedSlot]);

  const nudgeSelectedFocus = useCallback((dx, dy) => {
    const slot = selectedSlotData;
    if (!slot) return;
    const focusX = Number(slot.focusX ?? 0.5) + dx;
    const focusY = Number(slot.focusY ?? 0.5) + dy;
    updateSelectedSlotTransform({ focusX, focusY });
  }, [selectedSlotData, updateSelectedSlotTransform]);

  const handleSlotDrag = useCallback((slotIndex, deltaFocusX, deltaFocusY) => {
    if (!selectedSlot) return;
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const slot = next[selectedSlot.layoutIndex]?.pages?.[selectedSlot.pageArrayIndex]?.images?.[selectedSlot.slotIndex];
      if (!slot) return prev;
      slot.focusX = clamp((slot.focusX ?? 0.5) + deltaFocusX, 0, 1);
      slot.focusY = clamp((slot.focusY ?? 0.5) + deltaFocusY, 0, 1);
      return next;
    });
    setHasDraftChanges(true);
  }, [selectedSlot]);

  const handleSlotZoom = useCallback((slotIndex, delta) => {
    if (!selectedSlot) return;
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const slot = next[selectedSlot.layoutIndex]?.pages?.[selectedSlot.pageArrayIndex]?.images?.[selectedSlot.slotIndex];
      if (!slot) return prev;
      slot.zoom = clamp((slot.zoom ?? 1) + delta * 3, 0.1, 5);
      return next;
    });
    setHasDraftChanges(true);
  }, [selectedSlot]);

  const handleSlotRotate = useCallback((slotIndex, deltaDeg) => {
    if (!selectedSlot) return;
    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const slot = next[selectedSlot.layoutIndex]?.pages?.[selectedSlot.pageArrayIndex]?.images?.[selectedSlot.slotIndex];
      if (!slot) return prev;
      slot.rotate = clamp((slot.rotate ?? 0) + deltaDeg, -180, 180);
      return next;
    });
    setHasDraftChanges(true);
  }, [selectedSlot]);

  const applyReplacementImage = useCallback(() => {
    if (!selectedSlot || !selectedReplacementImageId) return;
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const slot = next[selectedSlot.layoutIndex]?.pages?.[selectedSlot.pageArrayIndex]?.images?.[selectedSlot.slotIndex];
      if (!slot) return prev;
      slot.imageId = selectedReplacementImageId;
      slot._meta = { ...(slot._meta || {}), imageId: selectedReplacementImageId };
      return next;
    });
    setSelectedSlot((prev) => (prev ? { ...prev, imageId: selectedReplacementImageId } : prev));
    setHasDraftChanges(true);
    setEditMessage({ severity: 'success', text: 'Image swapped in selected slot.' });
  }, [selectedReplacementImageId, selectedSlot]);

  const swapCurrentSpreadLayout = useCallback(() => {
    if (!leftEntry || !rightEntry) return;
    setDraftLayouts((prev) => {
      const next = structuredClone(prev);
      const leftPages = next[leftEntry.layoutIndex]?.pages;
      const rightPages = next[rightEntry.layoutIndex]?.pages;
      if (!leftPages || !rightPages) return prev;
      const leftPage = leftPages[leftEntry.pageArrayIndex];
      leftPages[leftEntry.pageArrayIndex] = rightPages[rightEntry.pageArrayIndex];
      rightPages[rightEntry.pageArrayIndex] = leftPage;
      return next;
    });
    setHasDraftChanges(true);
    setSelectedSlot(null);
    setEditMessage({ severity: 'success', text: 'Current spread layout swapped (left/right).' });
  }, [leftEntry, rightEntry]);

  const saveDraftEdits = useCallback(async () => {
    if (!sessionId || !hasDraftChanges) return;
    setSavingEdits(true);
    try {
      await albumService.saveAlbumLayout(sessionId, draftLayouts);
      setHasDraftChanges(false);
      setEditMessage({ severity: 'success', text: 'Album edits saved.' });
      await loadLayout(true);
    } catch (err) {
      setEditMessage({ severity: 'error', text: err.message || 'Failed to save album edits.' });
    } finally {
      setSavingEdits(false);
    }
  }, [draftLayouts, hasDraftChanges, loadLayout, sessionId]);

  const applyRatingFilter = useCallback(() => {
    if (minRatingFilter === 0) return;
    const kept = draftLayouts.filter(layout => {
      const scene = storySceneById.get(layout.sceneId);
      return (scene?.themeRelevanceScore ?? 0) >= minRatingFilter;
    });
    const removed = draftLayouts.length - kept.length;
    setDraftLayouts(kept);
    setHasDraftChanges(true);
    setSpreadIndex(0);
    setMinRatingFilter(0);
    setEditMessage({ severity: 'success', text: `Removed ${removed} scene${removed !== 1 ? 's' : ''} below rating ★${minRatingFilter}. Click "Save Edits" to persist.` });
  }, [draftLayouts, minRatingFilter, storySceneById]);

  const exportAlbum = useCallback(() => {
    const payload = {
      sessionId,
      exportedAt: new Date().toISOString(),
      albumLayout: {
        ...(albumLayout || {}),
        layouts: draftLayouts,
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const exportAlbumSlug = (session?.name || sessionId).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
    link.download = `${exportAlbumSlug}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [albumLayout, draftLayouts, sessionId]);

  const exportPrintableFile = useCallback(async () => {
    try {
      const imageIdSet = new Set();
      allPages.forEach((entry) => {
        (entry.page.images || []).forEach((slot) => {
          const imageId = slot?._meta?.imageId || slot?.imageId;
          if (imageId) imageIdSet.add(imageId);
        });
      });

      const imageIds = Array.from(imageIdSet);
      const urlEntries = await Promise.all(
        imageIds.map(async (imageId) => {
          try {
            const data = await imageService.getDownloadUrl(imageId);
            return [imageId, data?.downloadUrl || null];
          } catch {
            return [imageId, null];
          }
        })
      );
      const imageUrlById = Object.fromEntries(urlEntries);

      const PAGE_W_MM = 297;
      const PAGE_H_MM = 210;
      const BLEED_MM = 3;
      const SAFE_MARGIN_MM = 5;
      const SIDE_W_MM = PAGE_W_MM + (BLEED_MM * 2);
      const SIDE_H_MM = PAGE_H_MM + (BLEED_MM * 2);
      const SPREAD_W_MM = SIDE_W_MM * 2;
      const SPREAD_H_MM = SIDE_H_MM;

      const esc = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

      const renderCropMarksHtml = () => `
        <div class="crop crop-tl-h"></div>
        <div class="crop crop-tl-v"></div>
        <div class="crop crop-tr-h"></div>
        <div class="crop crop-tr-v"></div>
        <div class="crop crop-bl-h"></div>
        <div class="crop crop-bl-v"></div>
        <div class="crop crop-br-h"></div>
        <div class="crop crop-br-v"></div>
      `;

      const renderAlbumPageHtml = (entry, pageNumber, { isCover = false } = {}) => {
        if (!entry) {
          return `
            <div class="print-side empty-side">
              ${renderCropMarksHtml()}
              <div class="album-page empty-page">${isCover ? '' : '<div class="empty-label">End of album</div>'}</div>
            </div>
          `;
        }

        const page = entry.page || {};
        const pageBg = page.backgroundColor || albumLayout?.backgroundColor || '#f8f5f0';

        const slotsHtml = (page.images || []).map((slot) => {
          const imageId = slot?._meta?.imageId || slot?.imageId;
          const imageUrl = imageId ? imageUrlById[imageId] : null;
          const borderRadius = Number(slot?.borderRadius || 0);
          const x = Number(slot?.x || 0);
          const y = Number(slot?.y || 0);
          const w = Number(slot?.width || 0);
          const h = Number(slot?.height || 0);
          const zIndex = Number(slot?.zIndex || 0);
          const zoom = Number(slot?.zoom || 1);
          const focusX = Number(slot?.focusX ?? 0.5);
          const focusY = Number(slot?.focusY ?? 0.5);
          const leftPctRaw = (0.5 - (focusX * zoom)) * 100;
          const topPctRaw = (0.5 - (focusY * zoom)) * 100;
          const minOffsetPct = -(zoom - 1) * 100;
          const leftPct = Math.max(minOffsetPct, Math.min(0, leftPctRaw));
          const topPct = Math.max(minOffsetPct, Math.min(0, topPctRaw));
          const imgStyle = [
            `width:${zoom * 100}%`,
            `height:${zoom * 100}%`,
            `left:${leftPct}%`,
            `top:${topPct}%`,
            `object-position:${focusX * 100}% ${focusY * 100}%`,
            ...(buildFilterCss(slot.filters || {}) ? [`filter:${buildFilterCss(slot.filters || {})}`] : []),
          ].join(';');

          return `<div class="slot" style="left:${x}mm;top:${y}mm;width:${w}mm;height:${h}mm;z-index:${zIndex};border-radius:${borderRadius}mm;">${imageUrl ? `<img src="${esc(imageUrl)}" alt="" style="${imgStyle}" />` : '<div class="missing">Missing image</div>'}</div>`;
        }).join('');

        const textHtml = (page.textBlocks || []).map((tb) => {
          const x = Number(tb?.x || 0);
          const y = Number(tb?.y || 0);
          const w = Number(tb?.width || 0);
          const fontSize = Number(tb?.fontSize || 3.5);
          const fontWeight = esc(tb?.fontWeight || 'normal');
          const color = esc(tb?.color || '#333');
          const align = esc(tb?.align || 'left');
          const text = esc(tb?.text || '');
          return `<div class="text" style="left:${x}mm;top:${y}mm;width:${w}mm;font-size:${fontSize}mm;font-weight:${fontWeight};color:${color};text-align:${align};">${text}</div>`;
        }).join('');

        return `
          <div class="print-side" style="background:${esc(pageBg)};">
            ${renderCropMarksHtml()}
            <div class="album-page" style="background:${esc(pageBg)};">
              <div class="safe-guide"></div>
              <div class="page-content">
                ${slotsHtml}
                ${textHtml}
              </div>
              ${pageNumber != null ? `<div class="page-number">Page ${pageNumber}</div>` : ''}
            </div>
          </div>
        `;
      };

      // Prepend a null cover page so spread 0 = blank-cover-left + page-1-right.
      // printPages indices: 0=null(cover), 1=allPages[0](p1), 2=allPages[1](p2), …
      const printPages = [null, ...allPages];
      const spreadCount = Math.ceil(printPages.length / 2);
      const spreadsHtml = Array.from({ length: spreadCount }).map((_, spreadIdx) => {
        const leftIdx = spreadIdx * 2;
        const rightIdx = leftIdx + 1;
        const leftEntry  = printPages[leftIdx]  || null;
        const rightEntry = printPages[rightIdx] || null;
        // Page numbers: index 0 = cover (no number), indices 1…N = pages 1…N
        const leftPageNum  = leftIdx  > 0 && leftIdx  <= allPages.length ? leftIdx  : null;
        const rightPageNum = rightIdx > 0 && rightIdx <= allPages.length ? rightIdx : null;
        const isCoverLeft  = spreadIdx === 0;
        const sceneEntry = leftEntry || rightEntry;
        const spreadTitle = sceneEntry?.sceneTitle || sceneEntry?.sceneName || '';
        const spreadMetaLabel = spreadTitle
          ? `${esc(spreadTitle)} — Spread ${spreadIdx + 1} of ${spreadCount}`
          : `Spread ${spreadIdx + 1} of ${spreadCount}`;
        return `
          <div class="spread-page">
            <div class="spread-meta">${spreadMetaLabel}</div>
            <div class="spread-body">
              ${renderAlbumPageHtml(leftEntry,  leftPageNum,  { isCover: isCoverLeft })}
              ${renderAlbumPageHtml(rightEntry, rightPageNum)}
            </div>
          </div>
        `;
      }).join('');

      const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Album Print Export - ${esc(sessionId)}</title>
  <style>
    @page { size: ${SPREAD_W_MM}mm ${SPREAD_H_MM}mm; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; background: #f0f0f0; }
    .spread-page { position: relative; width: ${SPREAD_W_MM}mm; height: ${SPREAD_H_MM}mm; page-break-after: always; overflow: hidden; background: #fff; }
    .spread-page:last-child { page-break-after: auto; }
    .spread-meta { position: absolute; left: 3mm; top: 2mm; z-index: 70; font-size: 8pt; color: rgba(0,0,0,0.45); }
    .spread-body { position: absolute; left: 0; top: 0; width: ${SPREAD_W_MM}mm; height: ${SPREAD_H_MM}mm; display: flex; }
    .print-side { position: relative; width: ${SIDE_W_MM}mm; height: ${SIDE_H_MM}mm; overflow: hidden; flex-shrink: 0; }
    .album-page { position: absolute; left: ${BLEED_MM}mm; top: ${BLEED_MM}mm; width: ${PAGE_W_MM}mm; height: ${PAGE_H_MM}mm; overflow: hidden; border: 0.2mm solid rgba(0,0,0,0.08); }
    .album-page.empty-page { background: #f3f3f3; display: flex; align-items: center; justify-content: center; }
    .empty-label { color: #777; font-size: 10pt; }
    .safe-guide { position: absolute; left: ${SAFE_MARGIN_MM}mm; top: ${SAFE_MARGIN_MM}mm; width: ${PAGE_W_MM - (SAFE_MARGIN_MM * 2)}mm; height: ${PAGE_H_MM - (SAFE_MARGIN_MM * 2)}mm; border: 0.15mm dashed rgba(0,90,255,0.45); pointer-events: none; z-index: 35; }
    .page-content { position: absolute; width: ${PAGE_W_MM}mm; height: ${PAGE_H_MM}mm; }
    .slot { position: absolute; overflow: hidden; background: #e9e9e9; }
    .slot img { position: absolute; object-fit: cover; display: block; }
    .missing { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #777; font-size: 9pt; }
    .text { position: absolute; line-height: 1.2; white-space: pre-wrap; text-shadow: 0 1px 2px rgba(255,255,255,0.7); }
    .page-number { position: absolute; right: 4mm; bottom: 3mm; font-size: 8pt; color: rgba(0,0,0,0.35); z-index: 50; }
    .crop { position: absolute; background: #111; z-index: 80; pointer-events: none; }
    .crop-tl-h { left: 0.5mm; top: ${BLEED_MM}mm; width: ${BLEED_MM - 0.5}mm; height: 0.2mm; }
    .crop-tl-v { left: ${BLEED_MM}mm; top: 0.5mm; width: 0.2mm; height: ${BLEED_MM - 0.5}mm; }
    .crop-tr-h { right: 0.5mm; top: ${BLEED_MM}mm; width: ${BLEED_MM - 0.5}mm; height: 0.2mm; }
    .crop-tr-v { right: ${BLEED_MM}mm; top: 0.5mm; width: 0.2mm; height: ${BLEED_MM - 0.5}mm; }
    .crop-bl-h { left: 0.5mm; bottom: ${BLEED_MM}mm; width: ${BLEED_MM - 0.5}mm; height: 0.2mm; }
    .crop-bl-v { left: ${BLEED_MM}mm; bottom: 0.5mm; width: 0.2mm; height: ${BLEED_MM - 0.5}mm; }
    .crop-br-h { right: 0.5mm; bottom: ${BLEED_MM}mm; width: ${BLEED_MM - 0.5}mm; height: 0.2mm; }
    .crop-br-v { right: ${BLEED_MM}mm; bottom: 0.5mm; width: 0.2mm; height: ${BLEED_MM - 0.5}mm; }
    @media screen { body { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 10px; } .spread-page { box-shadow: 0 2px 10px rgba(0,0,0,0.2); } }
    @media print { body { background: #fff; } .safe-guide { display: none; } .spread-page { margin: 0; box-shadow: none; } }
  </style>
</head>
<body>
  ${spreadsHtml}
</body>
</html>`;

      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const printAlbumSlug = (session?.name || sessionId).replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
      link.download = `${printAlbumSlug}-print-${new Date().toISOString().slice(0, 10)}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      setEditMessage({
        severity: 'success',
        text: 'Spread-based print file exported with bleed/crop marks/safe margins.',
      });
    } catch (err) {
      setEditMessage({ severity: 'error', text: err.message || 'Failed to export print file.' });
    }
  }, [allPages, albumLayout?.backgroundColor, sessionId]);

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">Loading album layout</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={onBack}>Back to Layout</Button>
      </Box>
    );
  }

  if (!albumLayout || allPages.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <PagesIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" gutterBottom>No layout generated yet</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Go back to the AI Page Layout step to generate your album design.
        </Typography>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={onBack}>Back to Layout</Button>
      </Box>
    );
  }

  const overlaySourceSpread = pageTurnState ? getSpreadEntries(pageTurnState.fromSpreadIndex) : null;
  const overlayTargetSpread = pageTurnState ? getSpreadEntries(pageTurnState.toSpreadIndex) : null;
  const overlayFrontPage = pageTurnState?.direction === 'next'
    ? overlaySourceSpread?.right?.page
    : overlaySourceSpread?.left?.page;
  const overlayBackPage = pageTurnState?.direction === 'next'
    ? overlayTargetSpread?.left?.page
    : overlayTargetSpread?.right?.page;
  const overlaySide = pageTurnState?.direction === 'next' ? 'right' : 'left';
  const overlayHingeOffsetPx = PREVIEW_GAP_PX / 2;
  const stagePageCount = Math.max(
    rightEntry ? 2 : leftEntry ? 1 : 0,
    overlaySourceSpread?.right || overlaySourceSpread?.left ? (overlaySourceSpread?.right ? 2 : 1) : 0,
  );
  const displayWidth = Math.round(PREVIEW_W_PX * responsiveDisplayScale);
  const displayHeight = Math.round(PREVIEW_H_PX * responsiveDisplayScale);
  const stageWidth = (displayWidth * Math.max(1, stagePageCount)) + (PREVIEW_GAP_PX * Math.max(0, stagePageCount - 1));
  const stageHeight = PAGE_LABEL_HEIGHT_PX + displayHeight;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={onBack}>Back to Layout</Button>
          <Button endIcon={<CheckIcon />} variant="contained" color="success" onClick={() => onNext({})}>Finish</Button>

          <Box>
          <Typography variant="h6" fontWeight="bold">Album Preview</Typography>
          <Typography variant="body2" color="text.secondary">{allPages.length} pages across {draftLayouts.length ?? 0} scenes</Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {spreadSceneName && <Chip label={spreadSceneName} size="small" color="primary" variant="outlined" />}

          <Button size="small" variant="outlined" startIcon={<ChangeLayoutIcon />}
            disabled={!leftEntry && !rightEntry}
            onClick={() => {
              const entry = leftEntry ?? rightEntry;
              if (entry) setChangeLayoutTarget({ layoutIndex: entry.layoutIndex, pageArrayIndex: entry.pageArrayIndex });
              setChangeLayoutDialogOpen(true);
            }}>Change Layout</Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportAlbum}>Export JSON</Button>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportPrintableFile}>Export Print File</Button>
          <Button size="small" variant="contained" startIcon={savingEdits ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} disabled={!hasDraftChanges || savingEdits} onClick={saveDraftEdits}>Save Edits</Button>

          <Tooltip title="Reload from database">
            <span>
              <IconButton size="small" onClick={() => loadLayout(true)} disabled={refreshing || savingEdits}>
                {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {editMessage && <Alert severity={editMessage.severity} onClose={() => setEditMessage(null)}>{editMessage.text}</Alert>}

      {failedScenes.length > 0 && (
        <Alert
          severity={failedScenes.some(f => f.quotaError) ? 'warning' : 'error'}
          onClose={() => setFailedScenes([])}
        >
          {failedScenes.some(f => f.quotaError)
            ? `OpenAI quota exceeded — ${failedScenes.length} scene${failedScenes.length > 1 ? 's' : ''} not generated: ${failedScenes.map(f => f.sceneName || f.sceneId).join(', ')}. Add credit to your OpenAI account and re-run those scenes.`
            : `${failedScenes.length} scene${failedScenes.length > 1 ? 's' : ''} failed to generate: ${failedScenes.map(f => f.sceneName || f.sceneId).join(', ')}. Error: ${failedScenes[0]?.error || 'unknown'}. Re-run those scenes.`
          }
        </Alert>
      )}

      {albumContext && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5 }}>
            Album context:
          </Typography>
          <Typography variant="body2" component="span">
            {albumContext}
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: selectedSlotData ? 'minmax(0, 1fr) 240px' : '1fr' }, alignItems: 'flex-start', gap: 2, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, minWidth: 0, width: '100%', maxWidth: '100%' }}>
          {/* Compact info row: pages | titles | ratings */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minHeight: 28 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {leftEntry ? `Pages ${leftPageNum}–${rightPageNum}` : (rightEntry ? `Page ${rightPageNum}` : '')} of {filteredPages.length}{totalSpreads > 1 ? `  · Spread ${spreadIndex + 1}/${totalSpreads}` : ''}{minRatingFilter > 0 ? ` (filtered from ${allPages.length})` : ''}
            </Typography>
            {(spreadSceneTitle || rightSceneTitle) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {spreadSceneTitle && (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, px: 1, py: 0.15, borderRadius: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                  >
                    {spreadSceneTitle}
                  </Typography>
                )}
                {rightSceneTitle && (
                  <>
                    <Typography variant="caption" color="text.disabled">|</Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, px: 1, py: 0.15, borderRadius: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                    >
                      {rightSceneTitle}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            {leftSceneRating && (
              <Chip size="small" label={`★${leftSceneRating.score}`} color={leftSceneRating.score >= 8 ? 'success' : leftSceneRating.score >= 5 ? 'warning' : 'error'} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} title={`${leftSceneRating.sceneName}: ${leftSceneRating.score}/10`} />
            )}
            {rightSceneRating && rightSceneRating.sceneName !== leftSceneRating?.sceneName && (
              <Chip size="small" label={`★${rightSceneRating.score}`} color={rightSceneRating.score >= 8 ? 'success' : rightSceneRating.score >= 5 ? 'warning' : 'error'} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} title={`${rightSceneRating.sceneName}: ${rightSceneRating.score}/10`} />
            )}
            {spreadTemplate && (
              <Tooltip title={`${rightTemplate && rightTemplate !== spreadTemplate ? 'Left page: ' : 'Template: '}${spreadTemplate} — click to open in template editor`}>
                <Chip
                  size="small"
                  label={spreadTemplate}
                  variant="outlined"
                  component="a"
                  href={`/admin/album-templates?highlight=${encodeURIComponent(spreadTemplate)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  sx={{ fontFamily: 'monospace', fontSize: '0.68rem', height: 20, cursor: 'pointer', textDecoration: 'none' }}
                />
              </Tooltip>
            )}
            {rightTemplate && rightTemplate !== spreadTemplate && (
              <Tooltip title={`Right page: ${rightTemplate} — click to open in template editor`}>
                <Chip
                  size="small"
                  label={rightTemplate}
                  variant="outlined"
                  component="a"
                  href={`/admin/album-templates?highlight=${encodeURIComponent(rightTemplate)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  sx={{ fontFamily: 'monospace', fontSize: '0.68rem', height: 20, cursor: 'pointer', textDecoration: 'none' }}
                />
              </Tooltip>
            )}
          </Stack>

          <Box ref={previewAreaRef} onClick={() => { setSelectedSlot(null); setSelectedSlotOriginal(null); setSelectedReplacementImageId(''); }} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', pb: 1, width: '100%', maxWidth: '100%', overflow: 'hidden', perspective: '2400px' }}>
            <Box sx={{ position: 'relative', width: stageWidth, height: stageHeight }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: `${PREVIEW_GAP_PX}px` }}>
                {leftEntry ? (
                  <ScaledPage
                    page={leftEntry.page}
                    displayScale={responsiveDisplayScale}
                    pageMarginMm={albumLayout.pageMarginMm ?? 0}
                    preloadedImageUrls={preloadedImageUrlById}
                    label={`Page ${leftPageNum}`}
                    onSlotClick={(slotIndex) => handleSelectSlot(leftEntry, slotIndex)}
                    onSlotDrag={handleSlotDrag}
                    onSlotZoom={handleSlotZoom}
                    onSlotRotate={handleSlotRotate}
                    selectedSlotIndex={selectedSlot && selectedSlot.layoutIndex === leftEntry.layoutIndex && selectedSlot.pageArrayIndex === leftEntry.pageArrayIndex ? selectedSlot.slotIndex : null}
                    onMoveBackward={() => moveLayoutByOffset(leftEntry.layoutIndex, -1)}
                    onMoveForward={() => moveLayoutByOffset(leftEntry.layoutIndex, 1)}
                    moveBackwardDisabled={leftEntry.layoutIndex === 0}
                    moveForwardDisabled={leftEntry.layoutIndex >= draftLayouts.length - 1}
                    onRemove={() => removeLayout(leftEntry.layoutIndex)}
                    removeDisabled={draftLayouts.length <= 1}
                    onChangeLayout={() => { setChangeLayoutTarget({ layoutIndex: leftEntry.layoutIndex, pageArrayIndex: leftEntry.pageArrayIndex }); setChangeLayoutDialogOpen(true); }}
                    onEditText={() => openEditText(leftEntry)}
                    onMoveTo={() => { setMoveToTargetLayout(leftEntry.layoutIndex); setMoveToPosition(leftEntry.layoutIndex + 1); setMoveToDialogOpen(true); }}
                    onSplit={draftLayouts[leftEntry.layoutIndex]?.pages?.length > 1 ? () => splitScene(leftEntry.layoutIndex) : null}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', visibility: 'hidden' }}>&nbsp;</Typography>
                    <EmptyPage displayScale={responsiveDisplayScale} label="Cover" />
                  </Box>
                )}

                {rightEntry ? (
                  <ScaledPage
                    page={rightEntry.page}
                    displayScale={responsiveDisplayScale}
                    pageMarginMm={albumLayout.pageMarginMm ?? 0}
                    preloadedImageUrls={preloadedImageUrlById}
                    label={`Page ${rightPageNum}`}
                    onSlotClick={(slotIndex) => handleSelectSlot(rightEntry, slotIndex)}
                    onSlotDrag={handleSlotDrag}
                    onSlotZoom={handleSlotZoom}
                    onSlotRotate={handleSlotRotate}
                    selectedSlotIndex={selectedSlot && selectedSlot.layoutIndex === rightEntry.layoutIndex && selectedSlot.pageArrayIndex === rightEntry.pageArrayIndex ? selectedSlot.slotIndex : null}
                    onMoveBackward={() => moveLayoutByOffset(rightEntry.layoutIndex, -1)}
                    onMoveForward={() => moveLayoutByOffset(rightEntry.layoutIndex, 1)}
                    moveBackwardDisabled={rightEntry.layoutIndex === 0}
                    moveForwardDisabled={rightEntry.layoutIndex >= draftLayouts.length - 1}
                    onRemove={() => removeLayout(rightEntry.layoutIndex)}
                    removeDisabled={draftLayouts.length <= 1}
                    onChangeLayout={() => { setChangeLayoutTarget({ layoutIndex: rightEntry.layoutIndex, pageArrayIndex: rightEntry.pageArrayIndex }); setChangeLayoutDialogOpen(true); }}
                    onEditText={() => openEditText(rightEntry)}
                    onMoveTo={() => { setMoveToTargetLayout(rightEntry.layoutIndex); setMoveToPosition(rightEntry.layoutIndex + 1); setMoveToDialogOpen(true); }}
                    onSplit={draftLayouts[rightEntry.layoutIndex]?.pages?.length > 1 ? () => splitScene(rightEntry.layoutIndex) : null}
                  />
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', visibility: 'hidden' }}>&nbsp;</Typography>
                    <EmptyPage displayScale={responsiveDisplayScale} label="Back cover" />
                  </Box>
                )}
              </Box>

              {overlayFrontPage && (
                <TurningPageOverlay
                  frontPage={overlayFrontPage}
                  backPage={overlayBackPage}
                  displayScale={responsiveDisplayScale}
                  pageMarginMm={albumLayout.pageMarginMm ?? 0}
                  preloadedImageUrls={preloadedImageUrlById}
                  direction={pageTurnState.direction}
                  side={overlaySide}
                  hingeOffsetPx={overlayHingeOffsetPx}
                  flipDurationMs={flipDurationMs}
                />
              )}

              {isPreparingTurn && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 8,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      px: 2,
                      py: 1.25,
                      borderRadius: 999,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      boxShadow: 2,
                    }}
                  >
                    <CircularProgress size={28} thickness={5} />
                    <Typography variant="caption" color="text.secondary">
                      Preparing page...
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
              <Tooltip title="Previous spread">
                <span>
                  <IconButton onClick={handlePrev} disabled={spreadIndex === 0 || isPreparingTurn || Boolean(pageTurnState)} size="large"><NavigateBefore /></IconButton>
                </span>
              </Tooltip>

              <Stack direction="row" spacing={0.5}>
                {Array.from({ length: totalSpreads }).map((_, i) => {
                  const leftEntry = filteredPages[i * 2];
                  const rightEntry = filteredPages[i * 2 + 1];
                  const leftLayout = leftEntry ? draftLayouts[leftEntry.layoutIndex] : null;
                  const rightLayout = rightEntry ? draftLayouts[rightEntry.layoutIndex] : null;
                  const isSameScene = leftEntry && rightEntry && leftEntry.layoutIndex === rightEntry.layoutIndex;
                  const leftScene = leftLayout ? storySceneById.get(leftLayout.sceneId) : null;
                  const rightScene = (!isSameScene && rightLayout) ? storySceneById.get(rightLayout.sceneId) : null;
                  const leftPageNum = i * 2 + 1;
                  const rightPageNum = i * 2 + 2;

                  // Build per-page rows: [{ label, name, rating, template }]
                  const pageRows = [];
                  if (leftLayout) {
                    pageRows.push({
                      pageNum: leftPageNum,
                      name: leftLayout.sceneTitle || leftLayout.sceneName || `Scene`,
                      rating: leftScene?.themeRelevanceScore ?? null,
                      template: leftLayout.templatePairName || null,
                    });
                  }
                  if (rightEntry) {
                    if (isSameScene) {
                      // second page of same scene — just note it
                      pageRows.push({ pageNum: rightPageNum, name: null, rating: null, template: null });
                    } else if (rightLayout) {
                      pageRows.push({
                        pageNum: rightPageNum,
                        name: rightLayout.sceneTitle || rightLayout.sceneName || `Scene`,
                        rating: rightScene?.themeRelevanceScore ?? null,
                        template: rightLayout.templatePairName || null,
                      });
                    }
                  }

                  const tipTitle = pageRows.length > 0 ? (
                    <Box sx={{ minWidth: 160 }}>
                      <Typography variant="caption" display="block" color="grey.400" sx={{ mb: 0.25 }}>
                        {`Spread ${i + 1} of ${totalSpreads}`}
                      </Typography>
                      {pageRows.map((row, ri) => (
                        <Box key={ri} sx={{ mt: ri > 0 ? 0.5 : 0, borderTop: ri > 0 ? '1px solid rgba(255,255,255,0.15)' : 'none', pt: ri > 0 ? 0.5 : 0 }}>
                          <Typography variant="caption" display="block" fontWeight={700}>
                            {`p${row.pageNum}${row.name ? ` — ${row.name}` : ' (continued)'}`}
                          </Typography>
                          {(row.rating != null || row.template) && (
                            <Typography variant="caption" display="block" color="grey.400">
                              {[row.rating != null ? `★${row.rating}/10` : null, row.template].filter(Boolean).join(' · ')}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : `Spread ${i + 1}`;
                  return (
                    <Tooltip key={i} title={tipTitle} placement="top" arrow>
                      <Box
                        onClick={() => goToSpread(i)}
                        sx={{
                          width: i === spreadIndex ? 20 : 8,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: i === spreadIndex ? 'primary.main' : 'grey.300',
                          cursor: isPreparingTurn || pageTurnState ? 'default' : 'pointer',
                          pointerEvents: isPreparingTurn || pageTurnState ? 'none' : 'auto',
                          opacity: isPreparingTurn || pageTurnState ? 0.55 : 1,
                          transition: 'all 0.2s',
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>

              <Tooltip title="Next spread">
                <span>
                  <IconButton onClick={handleNext} disabled={spreadIndex >= totalSpreads - 1 || isPreparingTurn || Boolean(pageTurnState)} size="large"><NavigateNext /></IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ maxWidth: 280, width: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', minWidth: 60 }}>
                Flip: {(flipDurationMs / 1000).toFixed(1)}s
              </Typography>
              <Slider
                min={400}
                max={3000}
                step={100}
                value={flipDurationMs}
                onChange={(_, v) => setFlipDurationMs(v)}
                size="small"
              />
            </Stack>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MoveSpreadIcon />}
              onClick={() => { setMoveSpreadPosition(spreadIndex + 1); setMoveSpreadDialogOpen(true); }}
            >
              Move Spread
            </Button>
          </Box>

          {storyScenes.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap sx={{ px: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>Filter by scene rating:</Typography>
              {[0, 5, 6, 7, 8, 9, 10].map(r => (
                <Chip
                  key={r}
                  size="small"
                  label={r === 0 ? 'All' : `★ ${r}+`}
                  onClick={() => { setMinRatingFilter(r); setSpreadIndex(0); }}
                  color={minRatingFilter === r ? 'primary' : 'default'}
                  variant={minRatingFilter === r ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
              {minRatingFilter > 0 && hiddenLayoutCount > 0 && (
                <Button size="small" variant="outlined" color="warning" onClick={applyRatingFilter}>
                  Remove {hiddenLayoutCount} scene{hiddenLayoutCount !== 1 ? 's' : ''} below ★{minRatingFilter}
                </Button>
              )}
              {minRatingFilter > 0 && hiddenLayoutCount === 0 && (
                <Typography variant="caption" color="success.main">All scenes meet this threshold</Typography>
              )}
            </Stack>
          )}
        </Box>

        {selectedSlotData && (
        <Paper variant="outlined" sx={{ width: { xs: '100%', lg: 240 }, flex: '0 0 auto', p: 2, alignSelf: 'stretch', position: { lg: 'sticky' }, top: { lg: 16 } }}>
          <Typography variant="subtitle2" gutterBottom>Selected Slot Controls</Typography>
          {selectedSlotData ? (
            <>
              {/* Image name + id */}
              {(() => {
                const img = sessionImageById.get(String(selectedSlot?.imageId || ''));
                const name = img?.name || img?.originalName || null;
                const imageId = selectedSlot?.imageId || null;
                return (name || imageId) ? (
                  <Box sx={{ mb: 1.5 }}>
                    {name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', wordBreak: 'break-all', fontStyle: 'italic' }}
                        title={name}
                      >
                        {name}
                      </Typography>
                    )}
                    {imageId && (
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ display: 'block', wordBreak: 'break-all', fontSize: '0.65rem', fontFamily: 'monospace' }}
                        title={imageId}
                      >
                        {imageId}
                      </Typography>
                    )}
                  </Box>
                ) : null;
              })()}
              {/* Zoom — range 0.1×–5× so image can shrink or enlarge */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Zoom: {Number(selectedSlotData.zoom ?? 1).toFixed(2)}×
                </Typography>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ zoom: Number(selectedSlotData.zoom ?? 1) - 0.05 })}><RemoveIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ zoom: Number(selectedSlotData.zoom ?? 1) + 0.05 })}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
              <Slider min={0.1} max={5} step={0.01} value={Number(selectedSlotData.zoom ?? 1)}
                onChange={(_, v) => updateSelectedSlotTransform({ zoom: v })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${Number(v).toFixed(2)}×`}
                sx={{ mb: 1.5 }} />

              {/* Center X */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Center X: {Number(selectedSlotData.focusX ?? 0.5).toFixed(3)}
                </Typography>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ focusX: Number(selectedSlotData.focusX ?? 0.5) - 0.01 })}><RemoveIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ focusX: Number(selectedSlotData.focusX ?? 0.5) + 0.01 })}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
              <Slider min={0} max={1} step={0.001} value={Number(selectedSlotData.focusX ?? 0.5)}
                onChange={(_, v) => updateSelectedSlotTransform({ focusX: v })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => Number(v).toFixed(3)}
                sx={{ mb: 1.5 }} />

              {/* Center Y */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Center Y: {Number(selectedSlotData.focusY ?? 0.5).toFixed(3)}
                </Typography>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ focusY: Number(selectedSlotData.focusY ?? 0.5) - 0.01 })}><RemoveIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ focusY: Number(selectedSlotData.focusY ?? 0.5) + 0.01 })}><AddIcon sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
              <Slider min={0} max={1} step={0.001} value={Number(selectedSlotData.focusY ?? 0.5)}
                onChange={(_, v) => updateSelectedSlotTransform({ focusY: v })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => Number(v).toFixed(3)}
                sx={{ mb: 1.5 }} />

              {/* Rotation */}
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Rotation: {Number(selectedSlotData.rotate ?? 0).toFixed(0)}°
                </Typography>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ rotate: Number(selectedSlotData.rotate ?? 0) - 5 })}><RotateLeftIcon sx={{ fontSize: 14 }} /></IconButton>
                <IconButton size="small" onClick={() => updateSelectedSlotTransform({ rotate: Number(selectedSlotData.rotate ?? 0) + 5 })}><RotateRightIcon sx={{ fontSize: 14 }} /></IconButton>
              </Stack>
              <Slider min={-180} max={180} step={1} value={Number(selectedSlotData.rotate ?? 0)}
                onChange={(_, v) => updateSelectedSlotTransform({ rotate: v })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${Number(v).toFixed(0)}°`}
                sx={{ mb: 1.5 }} />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Button size="small" variant="outlined" onClick={() => nudgeSelectedFocus(-0.01, 0)}>Left</Button>
                <Button size="small" variant="outlined" onClick={() => nudgeSelectedFocus(0.01, 0)}>Right</Button>
                <Button size="small" variant="outlined" onClick={() => nudgeSelectedFocus(0, -0.01)}>Up</Button>
                <Button size="small" variant="outlined" onClick={() => nudgeSelectedFocus(0, 0.01)}>Down</Button>
                <Button size="small" onClick={() => updateSelectedSlotTransform({ focusX: 0.5, focusY: 0.5 })}>Center</Button>
              </Stack>

              {selectedSlotOriginal && (
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  startIcon={<ResetIcon />}
                  fullWidth
                  onClick={() => updateSelectedSlotTransform(selectedSlotOriginal)}
                >
                  Reset to original
                </Button>
              )}
              {selectedSlot?.imageId && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  fullWidth
                  sx={{ mt: 1 }}
                  onClick={() => openImageDetail(selectedSlot.imageId)}
                >
                  View Image Details
                </Button>
              )}

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                Image Filters
              </Typography>

              {/* Brightness */}
              <Typography variant="caption" color="text.secondary">Brightness: {selectedSlotData.filters?.brightness ?? 100}%</Typography>
              <Slider min={0} max={200} step={1}
                value={selectedSlotData.filters?.brightness ?? 100}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { brightness: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                sx={{ mb: 1 }} />

              {/* Contrast */}
              <Typography variant="caption" color="text.secondary">Contrast: {selectedSlotData.filters?.contrast ?? 100}%</Typography>
              <Slider min={0} max={200} step={1}
                value={selectedSlotData.filters?.contrast ?? 100}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { contrast: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                sx={{ mb: 1 }} />

              {/* Saturation */}
              <Typography variant="caption" color="text.secondary">Saturation: {selectedSlotData.filters?.saturation ?? 100}%</Typography>
              <Slider min={0} max={200} step={1}
                value={selectedSlotData.filters?.saturation ?? 100}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { saturation: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                sx={{ mb: 1 }} />

              {/* Hue */}
              <Typography variant="caption" color="text.secondary">Hue Rotate: {selectedSlotData.filters?.hue ?? 0}°</Typography>
              <Slider min={0} max={360} step={1}
                value={selectedSlotData.filters?.hue ?? 0}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { hue: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}°`}
                sx={{ mb: 1 }} />

              {/* Grayscale */}
              <Typography variant="caption" color="text.secondary">Grayscale: {selectedSlotData.filters?.grayscale ?? 0}%</Typography>
              <Slider min={0} max={100} step={1}
                value={selectedSlotData.filters?.grayscale ?? 0}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { grayscale: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                sx={{ mb: 1 }} />

              {/* Sepia */}
              <Typography variant="caption" color="text.secondary">Sepia: {selectedSlotData.filters?.sepia ?? 0}%</Typography>
              <Slider min={0} max={100} step={1}
                value={selectedSlotData.filters?.sepia ?? 0}
                onChange={(_, v) => updateSelectedSlotTransform({ filters: { sepia: v } })}
                valueLabelDisplay="auto" valueLabelFormat={(v) => `${v}%`}
                sx={{ mb: 1 }} />

              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => updateSelectedSlotTransform({ filters: { brightness: 100, contrast: 100, saturation: 100, hue: 0, grayscale: 0, sepia: 0 } })}
              >
                Reset Filters
              </Button>
            </>
          ) : null}
        </Paper>
        )}
      </Box>

      {draftLayouts.length > 1 && (() => {
        const ROLE_PALETTE = { opening: '#7b1fa2', buildup: '#1565c0', climax: '#c62828', transition: '#558b2f', close: '#ef6c00' };
        const BUDGET_COLOR = { 1: 'default', 2: 'info', 3: 'warning', 4: 'error' };
        const ROLE_MUI    = { opening: 'secondary', buildup: 'primary', climax: 'error', transition: 'default', close: 'success' };
        const STYLE_ICONS  = { condensed: '⊞', balanced: '▪', relaxed: '○' };

        const currentSpreadLayoutIndices = new Set([
          virtualPages[spreadIndex * 2]?.layoutIndex,
          virtualPages[spreadIndex * 2 + 1]?.layoutIndex,
        ].filter(x => x != null));

        return (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Jump to scene</Typography>
            <Box
              ref={carouselRef}
              sx={{
                display: 'flex',
                gap: '6px',
                overflowX: 'auto',
                pb: 0.5,
                scrollBehavior: 'smooth',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 2 },
              }}
            >
              {draftLayouts.map((layout, li) => {
                const filteredFirstIdx = filteredPages.findIndex(entry => entry.layoutIndex === li);
                const targetSpread = filteredFirstIdx >= 0 ? Math.floor((filteredFirstIdx + 1) / 2) : 0;
                const isActive = currentSpreadLayoutIndices.has(li);
                const sceneTitle = layout.sceneTitle || null;
                const sceneName  = layout.sceneName  || `Scene ${li + 1}`;
                const storyScene = storySceneById?.get?.(layout.sceneId);
                const rating     = storyScene?.themeRelevanceScore ?? null;
                const emoji      = storyScene?.emoji || '📷';
                const planEntry  = planEntryBySceneId.get(layout.sceneId);
                const planBudget = planEntry?.budget ?? (layout.pages?.length ?? 1);
                const planRole   = planEntry?.role;
                const planStyle  = planEntry?.layout_style;
                const planSpread = planSpreadStartBySceneId.get(layout.sceneId);
                const roleColor  = planRole ? (ROLE_PALETTE[planRole] || null) : null;
                const label      = sceneTitle || sceneName;
                return (
                  <Box
                    key={li}
                    data-carousel-active={isActive ? 'true' : 'false'}
                    onClick={() => goToSpread(targetSpread)}
                    sx={{
                      flexShrink: 0,
                      width: 130,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: isActive ? 'primary.main' : 'divider',
                      borderLeft: `3px solid ${isActive ? '#1976d2' : (roleColor || 'transparent')}`,
                      bgcolor: isActive ? 'primary.50' : 'background.paper',
                      borderRadius: 1,
                      p: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      boxShadow: isActive ? 2 : 0,
                      transition: 'box-shadow 0.15s',
                      '&:hover': { bgcolor: isActive ? 'primary.100' : 'action.hover' },
                    }}
                  >
                    {/* Scene name row */}
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ overflow: 'hidden' }}>
                      <Typography sx={{ fontSize: '0.8rem', lineHeight: 1, flexShrink: 0 }}>{emoji}</Typography>
                      <Typography
                        variant="caption"
                        fontWeight={isActive ? 700 : 500}
                        noWrap
                        color={isActive ? 'primary.main' : 'text.primary'}
                        title={label}
                        sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}
                      >
                        {label}
                      </Typography>
                    </Stack>
                    {/* Indicator chips */}
                    <Stack direction="row" spacing={0.4} alignItems="center" useFlexGap flexWrap="wrap">
                      {rating != null && (
                        <Chip
                          label={`★${rating}`}
                          size="small"
                          color={rating >= 8 ? 'success' : rating >= 5 ? 'warning' : 'error'}
                          variant="outlined"
                          sx={{ height: 15, fontSize: '0.55rem', pointerEvents: 'none', '& .MuiChip-label': { px: 0.4 } }}
                        />
                      )}
                      <Chip
                        label={`${planBudget}p`}
                        size="small"
                        color={BUDGET_COLOR[planBudget] || 'default'}
                        variant="filled"
                        sx={{ height: 15, fontSize: '0.55rem', pointerEvents: 'none', '& .MuiChip-label': { px: 0.4 } }}
                      />
                      {planSpread != null && (
                        <Typography
                          variant="caption"
                          sx={{ fontSize: '0.55rem', fontWeight: 600, whiteSpace: 'nowrap', color: isActive ? 'primary.main' : 'text.disabled' }}
                          title="Album spread this scene starts on"
                        >
                          S{planSpread + 1}
                        </Typography>
                      )}
                      {planRole && (
                        <Chip
                          label={planRole}
                          size="small"
                          color={ROLE_MUI[planRole.toLowerCase()] || 'default'}
                          variant="outlined"
                          sx={{ height: 15, fontSize: '0.5rem', pointerEvents: 'none', textTransform: 'capitalize', '& .MuiChip-label': { px: 0.4 } }}
                        />
                      )}
                      {planStyle && STYLE_ICONS[planStyle] && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }} title={planStyle}>
                          {STYLE_ICONS[planStyle]}
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })()}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">Swap Images</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Per page:</Typography>
            <Select
              size="small"
              value={swapImagesPageSize}
              onChange={(e) => { setSwapImagesPageSize(Number(e.target.value)); setSwapImagesPage(0); }}
              sx={{ fontSize: '0.75rem', height: 24, '.MuiSelect-select': { py: 0 } }}
            >
              {[50, 100, 150, 200].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Click any image slot in the spread preview, then choose a session image below to swap it in.
          {` Showing ${sessionUsageStats.total} session image${sessionUsageStats.total !== 1 ? 's' : ''}.`}
          {` Used in album: ${sessionUsageStats.used}/${sessionUsageStats.total}.`}
        </Typography>

        {selectedSlot ? (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Selected slot: spread {spreadIndex + 1}, page {selectedSlot.pageArrayIndex + 1}, slot {selectedSlot.slotIndex + 1}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 1.5 }}>No slot selected yet.</Alert>
        )}

        {/* Swap button — sticky above the image grid so it's always visible */}
        {selectedSlot && selectedReplacementImageId && selectedReplacementImageId !== selectedSlot.imageId && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', py: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<SwapHorizIcon />}
              onClick={applyReplacementImage}
            >
              Swap With Selected Image
            </Button>
          </Box>
        )}

        {imagesLoading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 880, overflowY: 'auto', p: 0.5 }}>
            {sessionImages.slice(swapImagesPage * swapImagesPageSize, (swapImagesPage + 1) * swapImagesPageSize).map((image) => (
              (() => {
                const usage = imageUsageById.get(String(image.uniqueId));
                const pages = usage?.pages || [];
                const inAlbum = pages.length > 0;
                const pagesLabel = inAlbum
                  ? `Pages: ${pages.slice(0, 6).join(', ')}${pages.length > 6 ? ` +${pages.length - 6} more` : ''}`
                  : 'Not used in album';

                return (
                  <Box
                    key={image.uniqueId}
                    sx={{
                      width: 146,
                      border: '2px solid',
                      borderColor: selectedReplacementImageId === image.uniqueId ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 0.5,
                      bgcolor: selectedReplacementImageId === image.uniqueId ? 'action.hover' : 'transparent',
                    }}
                  >
                    <Tooltip title={image.name || image.originalName || ''} placement="top" arrow>
                      <Box
                        onClick={() => openImageDetail(image.uniqueId)}
                        sx={{ cursor: 'pointer', position: 'relative', '&:hover .info-overlay': { opacity: 1 } }}
                      >
                        <AuthenticatedImage
                          imageId={image.uniqueId}
                          type="thumbnail"
                          alt={image.name || image.originalName || 'session image'}
                          sx={{ width: '100%', height: 82, objectFit: 'cover', borderRadius: 0.75, display: 'block' }}
                        />
                        <Box className="info-overlay" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}>
                          <InfoIcon sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                      </Box>
                    </Tooltip>
                    <Typography variant="caption" noWrap display="block" sx={{ mt: 0.5, fontWeight: 600 }}>
                      {image.name || image.originalName}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                      <Chip
                        size="small"
                        color={inAlbum ? 'success' : 'default'}
                        variant={inAlbum ? 'filled' : 'outlined'}
                        label={inAlbum ? 'In album' : 'Not in album'}
                        onClick={inAlbum ? () => { const p = pages[0]; if (p != null) goToSpread(Math.floor((p - 1) / 2)); } : undefined}
                        sx={{ height: 20, cursor: inAlbum ? 'pointer' : 'default' }}
                      />
                    </Stack>
                    {inAlbum && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {pagesLabel}
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant={selectedReplacementImageId === image.uniqueId ? 'contained' : 'outlined'}
                      onClick={() => setSelectedReplacementImageId(image.uniqueId)}
                      sx={{ mt: 0.5, width: '100%', py: 0.25, fontSize: '0.68rem', minHeight: 22 }}
                    >
                      {selectedReplacementImageId === image.uniqueId ? 'Selected' : 'Select'}
                    </Button>
                  </Box>
                );
              })()
            ))}
          </Box>
        )}

        {/* Swap images pagination footer */}
        {sessionImages.length > swapImagesPageSize && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
            <IconButton size="small" onClick={() => setSwapImagesPage((p) => Math.max(0, p - 1))} disabled={swapImagesPage === 0}>
              <NavigateBefore fontSize="small" />
            </IconButton>
            <Typography variant="caption">
              {swapImagesPage * swapImagesPageSize + 1}–{Math.min((swapImagesPage + 1) * swapImagesPageSize, sessionImages.length)} of {sessionImages.length}
            </Typography>
            <IconButton size="small" onClick={() => setSwapImagesPage((p) => Math.min(Math.floor((sessionImages.length - 1) / swapImagesPageSize), p + 1))} disabled={(swapImagesPage + 1) * swapImagesPageSize >= sessionImages.length}>
              <NavigateNext fontSize="small" />
            </IconButton>
          </Stack>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<SwapHorizIcon />}
            disabled={!selectedSlot || !selectedReplacementImageId}
            onClick={applyReplacementImage}
          >
            Swap With Selected Image
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2">Album Highlights (from Step 5)</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Per page:</Typography>
            <Select
              size="small"
              value={highlightsPageSize}
              onChange={(e) => { setHighlightsPageSize(Number(e.target.value)); setHighlightsPage(0); }}
              sx={{ fontSize: '0.75rem', height: 24, '.MuiSelect-select': { py: 0 } }}
            >
              {[50, 100, 150, 200].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Top highlight images selected in Story Builder, with their current placement status in this album layout.
          {` Used in album: ${highlightUsageStats.used}/${highlightUsageStats.total}.`}
        </Typography>

        {highlightsLoading ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CircularProgress size={20} />
          </Box>
        ) : albumHighlights.length === 0 ? (
          <Alert severity="info">No highlights found from Step 5 story timeline.</Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxHeight: 1040, overflowY: 'auto', p: 0.5 }}>
            {albumHighlights.slice(highlightsPage * highlightsPageSize, (highlightsPage + 1) * highlightsPageSize).map((highlight, index) => {
              const imageId = String(highlight?.imageId || '');
              const usage = imageUsageById.get(imageId);
              const pages = usage?.pages || [];
              const inAlbum = pages.length > 0;
              const imageMeta = sessionImageById.get(imageId);
              const score = Number(highlight?.imageImportanceScore || 0);
              const pagesLabel = inAlbum
                ? `Pages: ${pages.slice(0, 6).join(', ')}${pages.length > 6 ? ` +${pages.length - 6} more` : ''}`
                : 'Not used in album';

              return (
                <Box
                  key={`${imageId || 'highlight'}-${index}`}
                  sx={{
                    width: 176,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.75,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Tooltip title={imageMeta?.name || imageMeta?.originalName || ''} placement="top" arrow>
                    <Box
                      onClick={() => imageId && openImageDetail(imageId)}
                      sx={{ cursor: imageId ? 'pointer' : 'default', position: 'relative', '&:hover .info-overlay': { opacity: 1 } }}
                    >
                      <AuthenticatedImage
                        imageId={imageId}
                        type="thumbnail"
                        alt={imageMeta?.name || imageMeta?.originalName || `highlight ${index + 1}`}
                        sx={{ width: '100%', height: 92, objectFit: 'cover', borderRadius: 0.75, display: 'block' }}
                      />
                      {imageId && (
                        <Box className="info-overlay" sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s' }}>
                          <InfoIcon sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                      )}
                    </Box>
                  </Tooltip>

                  <Typography variant="caption" noWrap display="block" sx={{ mt: 0.75, fontWeight: 600 }}>
                    {imageMeta?.name || imageMeta?.originalName || `Image ${index + 1}`}
                  </Typography>

                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={`Score ${score || '-'}/10`}
                      color="warning"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                    <Chip
                      size="small"
                      color={inAlbum ? 'success' : 'default'}
                      variant={inAlbum ? 'filled' : 'outlined'}
                      label={inAlbum ? 'In album' : 'Not in album'}
                      onClick={inAlbum ? () => { const p = pages[0]; if (p != null) goToSpread(Math.floor((p - 1) / 2)); } : undefined}
                      sx={{ height: 20, cursor: inAlbum ? 'pointer' : 'default' }}
                    />
                  </Stack>

                  {inAlbum && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {pagesLabel}
                    </Typography>
                  )}

                  {highlight?.reason && (
                    <Tooltip title={highlight.reason} placement="top" arrow>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mt: 0.35, fontStyle: 'italic', cursor: 'help' }}
                        noWrap
                      >
                        {highlight.reason}
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Highlights pagination footer */}
        {albumHighlights.length > highlightsPageSize && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1 }}>
            <IconButton size="small" onClick={() => setHighlightsPage((p) => Math.max(0, p - 1))} disabled={highlightsPage === 0}>
              <NavigateBefore fontSize="small" />
            </IconButton>
            <Typography variant="caption">
              {highlightsPage * highlightsPageSize + 1}–{Math.min((highlightsPage + 1) * highlightsPageSize, albumHighlights.length)} of {albumHighlights.length}
            </Typography>
            <IconButton size="small" onClick={() => setHighlightsPage((p) => Math.min(Math.floor((albumHighlights.length - 1) / highlightsPageSize), p + 1))} disabled={(highlightsPage + 1) * highlightsPageSize >= albumHighlights.length}>
              <NavigateNext fontSize="small" />
            </IconButton>
          </Stack>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1 }}>
        <Button startIcon={<ArrowBack />} variant="outlined" onClick={onBack}>Back to Layout</Button>
        <Button endIcon={<CheckIcon />} variant="contained" color="success" onClick={() => onNext({})}>Finish</Button>
      </Box>

      {/* ── Change Layout Dialog ── */}
      <Dialog
        open={changeLayoutDialogOpen}
        onClose={() => { setChangeLayoutDialogOpen(false); setChangeLayoutWizard(null); }}
        maxWidth="md"
        fullWidth
      >
        {!changeLayoutWizard ? (() => {
          // Helper: get current page's slot images
          const tgt = changeLayoutTarget;
          const currentPage = tgt ? draftLayouts[tgt.layoutIndex]?.pages?.[tgt.pageArrayIndex] : null;
          const pageSlotImages = (currentPage?.images || [])
            .map((s) => { const id = s._meta?.imageId || s.imageId; return id ? { imageId: String(id), _meta: s._meta } : null; })
            .filter(Boolean);
          const currentPageSlotCount = currentPage?.images?.length ?? 0;

          // Compute fingerprint of current page to detect "current" template
          const currentFp = (currentPage?.images || [])
            .map((s) => `${Math.round(s.x)},${Math.round(s.y)},${Math.round(s.width)},${Math.round(s.height)}`).join('|');

          const handleTemplateClick = (templateSlots) => {
            if (!tgt) return;
            const newSlotCount = templateSlots.length;
            if (newSlotCount < currentPageSlotCount) {
              setChangeLayoutWizard({
                step: 'select_keep',
                templateSlots,
                allImages: pageSlotImages,
                newSlotCount,
                selectedIds: pageSlotImages.slice(0, newSlotCount).map((i) => i.imageId),
              });
            } else if (newSlotCount > pageSlotImages.length) {
              const candidates = getCandidateImages(tgt.layoutIndex, tgt.pageArrayIndex);
              setChangeLayoutWizard({
                step: 'select_add',
                templateSlots,
                allImages: [...pageSlotImages, ...candidates.filter((c) => !pageSlotImages.find((p) => p.imageId === c.imageId))],
                newSlotCount,
                selectedIds: pageSlotImages.map((i) => i.imageId),
              });
            } else {
              changePageLayout(tgt, templateSlots);
            }
          };

          const renderTemplateGrid = (templates) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, maxHeight: 440, overflowY: 'auto', p: 0.5 }}>
              {templates.map((tmpl, i) => {
                const fp = tmpl.slots.map((s) => `${s.x},${s.y},${s.width},${s.height}`).join('|');
                const isCurrent = fp === currentFp;
                return (
                  <Tooltip key={tmpl.id || i} title={tmpl.description || ''} placement="top" arrow>
                  <Box
                    onClick={isCurrent ? undefined : () => handleTemplateClick(tmpl.slots)}
                    sx={{
                      cursor: isCurrent ? 'default' : 'pointer',
                      border: '2px solid',
                      borderColor: isCurrent ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 1,
                      bgcolor: isCurrent ? 'primary.50' : 'background.paper',
                      '&:hover': isCurrent ? {} : { borderColor: 'primary.light', bgcolor: 'action.hover' },
                      width: 120,
                      textAlign: 'center',
                    }}
                  >
                    <LayoutThumbnail slots={tmpl.slots} selected={isCurrent} />
                    <Typography variant="caption" display="block" fontWeight={600} noWrap sx={{ mt: 0.5 }}>
                      {tmpl.name}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {tmpl.slotCount} slot{tmpl.slotCount !== 1 ? 's' : ''}{isCurrent ? ' · current' : ''}
                    </Typography>
                  </Box>
                  </Tooltip>
                );
              })}
              {templates.length === 0 && (
                <Typography variant="body2" color="text.secondary">No layouts available.</Typography>
              )}
            </Box>
          );

          const activeCat = ALBUM_TEMPLATE_CATEGORIES[changeLayoutTab];
          const tabTemplates = activeCat?.id === 'album'
            ? uniquePageTemplates
            : dbPageTemplates.length > 0
              ? dbPageTemplates.filter((t) => t.category === activeCat?.id)
              : ALBUM_PAGE_TEMPLATES.filter((t) => t.category === activeCat?.id);

          return (
            <>
              <DialogTitle>
                Change Page Layout
                {tgt && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Page {allPages.findIndex((p) => p.layoutIndex === tgt.layoutIndex && p.pageArrayIndex === tgt.pageArrayIndex) + 1}
                    {' · '}{currentPageSlotCount} current slot{currentPageSlotCount !== 1 ? 's' : ''}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent sx={{ pt: 0.5 }}>
                <Tabs value={changeLayoutTab} onChange={(_, v) => setChangeLayoutTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  {ALBUM_TEMPLATE_CATEGORIES.map((cat) => (
                    <Tab key={cat.id} label={cat.label} />
                  ))}
                </Tabs>
                {activeCat?.id === 'album' && uniquePageTemplates.length === 0 ? (
                  <Alert severity="info">No per-page templates found. Save your album first to load them.</Alert>
                ) : renderTemplateGrid(tabTemplates)}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setChangeLayoutDialogOpen(false); setChangeLayoutWizard(null); }}>Close</Button>
              </DialogActions>
            </>
          );
        })() : (() => {
          const wiz = changeLayoutWizard;
          const isKeep = wiz.step === 'select_keep';
          const maxSelect = wiz.newSlotCount;
          const canApply = wiz.selectedIds.length === maxSelect;

          const toggleId = (id) => setChangeLayoutWizard((prev) => {
            if (!prev) return prev;
            const sel = prev.selectedIds.includes(id)
              ? prev.selectedIds.filter((s) => s !== id)
              : (prev.selectedIds.length < maxSelect ? [...prev.selectedIds, id] : prev.selectedIds);
            return { ...prev, selectedIds: sel };
          });

          return (
            <>
              <DialogTitle>
                {isKeep ? 'Select Images to Keep' : 'Select Images to Fill Slots'}
              </DialogTitle>
              <DialogContent sx={{ pt: 1 }}>
                <Alert severity={isKeep ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  {isKeep
                    ? <>New layout has <strong>{maxSelect}</strong> slot{maxSelect !== 1 ? 's' : ''} — fewer than the current {wiz.allImages.length}. Select exactly <strong>{maxSelect}</strong> to keep:</>
                    : <>New layout has <strong>{maxSelect}</strong> slots — more than the current {wiz.allImages.filter((_, i) => i < (changeLayoutTarget ? draftLayouts[changeLayoutTarget.layoutIndex]?.pages?.[changeLayoutTarget.pageArrayIndex]?.images?.length ?? 0 : 0)).length} images. Select images from this page and adjacent scenes to fill all slots:</>
                  }
                </Alert>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  {wiz.selectedIds.length} / {maxSelect} selected — selection order determines slot assignment
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 400, overflowY: 'auto', p: 0.5 }}>
                  {wiz.allImages.map(({ imageId }, idx) => {
                    const isChecked = wiz.selectedIds.includes(imageId);
                    const slotNum = isChecked ? wiz.selectedIds.indexOf(imageId) + 1 : null;
                    const isCurrentPage = idx < (isKeep ? wiz.allImages.length : (changeLayoutTarget ? draftLayouts[changeLayoutTarget.layoutIndex]?.pages?.[changeLayoutTarget.pageArrayIndex]?.images?.length ?? 0 : 0));
                    return (
                      <Box
                        key={imageId}
                        onClick={() => toggleId(imageId)}
                        sx={{
                          cursor: 'pointer', border: '2px solid', position: 'relative',
                          borderColor: isChecked ? 'primary.main' : 'divider', borderRadius: 1, p: 0.5,
                          '&:hover': { borderColor: 'primary.light' },
                          opacity: (!isChecked && wiz.selectedIds.length >= maxSelect) ? 0.45 : 1,
                        }}
                      >
                        <AuthenticatedImage
                          imageId={imageId} type="thumbnail" alt="candidate"
                          sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 0.5, display: 'block' }}
                        />
                        {isChecked && (
                          <Box sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'primary.main', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 700, lineHeight: 1 }}>{slotNum}</Typography>
                          </Box>
                        )}
                        <Typography variant="caption" display="block" sx={{ textAlign: 'center', mt: 0.5, fontSize: '0.65rem', color: isCurrentPage ? 'text.primary' : 'text.secondary' }}>
                          {isCurrentPage ? `Slot ${idx + 1}` : 'Adjacent'}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setChangeLayoutWizard(null)}>Back</Button>
                <Button
                  variant="contained"
                  disabled={!canApply}
                  onClick={() => {
                    const orderedImages = wiz.selectedIds.map((id) => wiz.allImages.find((img) => img.imageId === id)).filter(Boolean);
                    changePageLayout(changeLayoutTarget, wiz.templateSlots, orderedImages);
                  }}
                >
                  Apply Layout
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Image Detail Dialog ── */}
      <ImageAnalysisDetailsDialog
        open={imageDetailDialog.open}
        onClose={() => setImageDetailDialog({ open: false, imageId: null })}
        imageId={imageDetailDialog.imageId}
      />

      {/* ── Edit Text Dialog ── */}
      <Dialog open={editTextDialogOpen} onClose={() => setEditTextDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Text</DialogTitle>
        <DialogContent>
          <TextField
            label="Scene Name"
            value={editTextValues.sceneName}
            onChange={(e) => setEditTextValues((v) => ({ ...v, sceneName: e.target.value }))}
            fullWidth
            sx={{ mt: 1, mb: 2 }}
          />
          {editTextValues.textBlocks.length > 0 ? (
            editTextValues.textBlocks.map((tb) => (
              <TextField
                key={tb.index}
                label={`Text Block ${tb.index + 1}`}
                value={tb.text}
                onChange={(e) => setEditTextValues((v) => ({
                  ...v,
                  textBlocks: v.textBlocks.map((b) => b.index === tb.index ? { ...b, text: e.target.value } : b),
                }))}
                fullWidth
                multiline
                rows={2}
                sx={{ mb: 1.5 }}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">No text blocks on this page.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTextDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditText}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── Move Scene to Position Dialog ── */}
      <Dialog open={moveToDialogOpen} onClose={() => setMoveToDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Scene to Position</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move scene to position (1–{draftLayouts.length}):
          </Typography>
          <Select
            value={moveToPosition}
            onChange={(e) => setMoveToPosition(Number(e.target.value))}
            fullWidth
            size="small"
          >
            {draftLayouts.map((layout, i) => (
              <MenuItem key={i} value={i + 1}>
                {`Position ${i + 1}: ${layout.sceneName || `Scene ${i + 1}`}${moveToTargetLayout === i ? ' (current)' : ''}`}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveToDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => moveLayoutToIndex(moveToTargetLayout, moveToPosition - 1)}>Move</Button>
        </DialogActions>
      </Dialog>

      {/* ── Move Spread to Position Dialog ── */}
      <Dialog open={moveSpreadDialogOpen} onClose={() => setMoveSpreadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Spread to Position</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move the current spread (both pages together) to spread position (1–{totalSpreads}):
          </Typography>
          <Select
            value={moveSpreadPosition}
            onChange={(e) => setMoveSpreadPosition(Number(e.target.value))}
            fullWidth
            size="small"
          >
            {Array.from({ length: totalSpreads }).map((_, i) => (
              <MenuItem key={i} value={i + 1}>
                {`Spread ${i + 1}${i === spreadIndex ? ' (current)' : ''}`}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveSpreadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => moveSpreadToIndex(leftEntry?.layoutIndex, rightEntry?.layoutIndex, moveSpreadPosition - 1)}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WizardStep6Preview;
